import { currentDivisionId } from 'common/src/stores/useFiltersStore';
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Icon } from '@iconify/react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Button,
  Skeleton,
  Select,
  SelectItem,
  Autocomplete,
  AutocompleteItem,
  Divider,
  Chip,
  Spinner,
  Input,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  DropdownSection,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Switch,
  SortDescriptor,
} from '@nextui-org/react';
import { useLocation } from 'wouter';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import {
  getSettlementsByExporterId,
  getSettlementById,
  getSettlementDetailsPageRPC,
  getSettlementSourceHierarchyRPC,
  SourceSettlementDocumentView,
  setSettlementReceiverForLegacyName,
} from 'common/src/api/settlements';
import {
  getCustomCostCategoriesEnabled,
  getExporterCostCategories,
  getSettlementExporterCosts,
  getSettlementSourceExporterCosts,
  isExporterCostViewSeason,
} from 'common/src/api/exporter-cost-categories';
import CostStandardToggle from '../../../components/settlements/cost-standard-toggle';
import { CostStandard } from '../../../hooks/useExporterCostView';
import { toExporterCostStandardRows } from '../../../utils/exporter-cost-export';
import {
  ExporterReceiver,
  Settlement,
  ShipmentBox,
} from 'common/src/utils/types';
import {
  buildSettlementQuadratureProductKey,
  buildShipmentQuadratureProductKey,
  normalizeQuadratureReference,
} from 'common/src/utils/quadrature';
import { hasSettlementKilosMismatch } from 'common/src/utils/settlement-detail-validation';
import { supabase } from 'common/src/supabase';
import { getShipmentBoxesForExport } from 'common/src/api/shipments';
import SettlementsLights from '../../../components/pallets/settlements-lights';
import { format, getISOWeek, addHours } from 'date-fns';
import {
  calculateWeightedAverage,
  getSettlementIncoterm,
  getWeekStart,
} from 'common/src/utils/functions';
import {
  calculateFOBSettlement,
  formatMoneyUSD,
  formatNumber,
} from '../../../utils/functions';
import EditSettlementDialog from '../../../components/settlements/edit-settlement-dialog';
import {
  logUserAction,
  getUserActions,
} from '../../../services/actionsService';
import * as XLSX from 'xlsx';
import useFilterStore from '../../../../../common/src/stores/useFiltersStore';
import SeasonFilter from '../../../../../common/src/components/filters/SeasonFilter';
import SinisterSettlementDialog from '../../../components/settlements/sinister-settlement-dialog';
import BlockedSettlementDialog from '../../../components/settlements/blocked-settlement-dialog';
import { toast } from 'react-toastify';
import useDataStore from '../../../../../common/src/stores/useDataStore';
import SettlementHistoryDrawer from '../../../components/settlements/settlement-history-drawer';
import { getSettlementHistoryCounts } from '../../../services/historyService';
import ExcelUploadModal from '../../../components/settlements/excel-upload-modal';
import ManualUploadModal from '../../../components/settlements/manual-upload-modal';
import ExcelMassiveUploadModal from '../../../components/settlements/excel-massive-upload-modal';
import MassiveRevenueUploadModal from '../../../components/settlements/massive-revenue-upload-modal';
import MassiveCostsUploadModal from '../../../components/settlements/massive-costs-upload-modal';
import MultiShipmentUploadModal from '../../../components/settlements/multi-shipment-upload-modal';
import SettlementSourceViews, {
  SettlementSourceViewMode,
} from '../../../components/settlements/settlement-source-views';
import ModalSlideFilter from '../../../../../common/src/components/filters/modal-sidebar-filter';
import Header from '../../../components/layouts/Header';
import {
  Alert,
  EmptyState,
  FiltersToggle,
  LoadingState,
  PageLayout,
  useConfirm,
} from '../../../components/ui';
import ContainerTrackingSyncButton from '../../../components/settlements/container-tracking-sync-button';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import useShipmentsStore from '../../../store/useShipmentsStore';
import useFruticStore from '../../../store/useFruticStore';
import { UNIFIED_TABLE_CLASSNAMES } from '../../../utils/table-styles';
import {
  addReceiverToExporter,
  getReceiversByExporter,
} from 'common/src/api/exporterReceivers';

const prepareSettlementsData = (
  settlements: Settlement[],
  t: (key: string) => string
) => {
  return settlements.map((settlement) => ({
    [t('Referencia')]: settlement.reference_number,
    [t('Cliente')]: settlement.receiver,
    [t('Tipo de Negocio')]: settlement.business_type || '',
    [t('Orden de Venta')]: settlement.sale_order || '',
    [t('Siniestro')]: settlement.is_sinister ? 'Si' : 'No',
    [t('Responsable Siniestro')]: settlement.is_sinister
      ? t(settlement.sinister_responsible || '') || ''
      : '',
    [t('Días Retraso Siniestro')]: settlement.sinister_delay_days || 0,
    [t('Nave')]: settlement.ship_trip?.ship?.name || t('Sin embarque'),
    [t('Fecha Venta')]: format(new Date(settlement.date_sold), 'dd/MM/yyyy'),
    [t('Kilos Totales')]: settlement.settlement_details.reduce(
      (sum, detail) => sum + (detail.kilos || 0),
      0
    ),
    [t('Incoterm')]:
      getSettlementIncoterm(settlement) === 'pre_liquidacion'
        ? t('Pre Liquidación')
        : getSettlementIncoterm(settlement).toUpperCase(),
    [t('Incoterm Original')]:
      settlement.original_incoterm?.toUpperCase() || 'N/A',
    [t('Liquidación FOB')]: calculateFOBSettlement(settlement),
    [t('Diferencia vs Mercado ETA %')]:
      calculateWeightedAverage(
        settlement.settlement_details.map((detail) => ({
          kilos: detail.kilos,
          value: detail.difference_vs_market_eta,
        })),
        (item) => item.value
      ).toFixed(2) + '%',
    // Suma absoluta en moneda local siguiendo la fórmula de "VS MERCADO ({currency})" del análisis de venta
    [t('Diferencia vs Mercado ETA $')]: (() => {
      let sum = 0;
      (settlement.settlement_details || []).forEach((detail) => {
        const diffPercentage = detail.difference_vs_market_eta as
          | number
          | null
          | undefined;
        const localTotalPrice = detail.local_total_price as number | null;
        if (
          typeof diffPercentage !== 'number' ||
          typeof localTotalPrice !== 'number' ||
          !isFinite(diffPercentage) ||
          !isFinite(localTotalPrice) ||
          localTotalPrice === 0
        ) {
          return;
        }
        const percentage = diffPercentage / 100;
        const denominator = 1 + percentage;
        if (denominator === 0 || !isFinite(denominator)) return;
        const marketPrice = localTotalPrice / denominator;
        const absoluteDifference = (marketPrice - localTotalPrice) * -1;
        if (isFinite(absoluteDifference)) sum += absoluteDifference;
      });
      return sum;
    })(),
  }));
};

const prepareSettlementDetailsData = (
  settlements: Settlement[],
  t: (key: string) => string,
  shipmentBoxes?: ShipmentBox[]
) => {
  // Build a lookup map from reference_number to first matching shipment box
  const boxByRef = new Map<string, ShipmentBox>();
  if (shipmentBoxes) {
    shipmentBoxes.forEach((sb) => {
      if (!boxByRef.has(sb.reference_number)) {
        boxByRef.set(sb.reference_number, sb);
      }
    });
  }

  const details: Array<Record<string, string | number | null>> = [];
  settlements.forEach((settlement) => {
    const matchedBox = boxByRef.get(settlement.reference_number);
    const shipType =
      settlement.ship_trip?.ship?.type || matchedBox?.ship_type || '';

    settlement.settlement_details.forEach((detail) => {
      details.push({
        [t('Referencia')]: settlement.reference_number,
        [t('Factura')]: matchedBox?.invoice_number || '',
        [t('Cliente')]: settlement.receiver,
        [t('Tipo de Negocio')]: settlement.business_type || '',
        [t('Orden de Venta')]: settlement.sale_order || '',
        [t('Siniestro')]: settlement.is_sinister ? 'Si' : 'No',
        [t('Responsable Siniestro')]: settlement.is_sinister
          ? t(settlement.sinister_responsible || '') || ''
          : '',
        [t('Días Retraso Siniestro')]: settlement.sinister_delay_days || 0,
        [t('Nave')]: settlement.ship_trip?.ship?.name || t('Sin embarque'),
        [t('Tipo de Nave')]: shipType,
        [t('Booking')]:
          matchedBox?.booking_number || matchedBox?.booking_awb || '',
        [t('AWB')]: matchedBox?.awb_number || '',
        [t('Región')]: matchedBox?.sale_region || '',
        [t('País Destino')]:
          settlement.ship_trip?.market?.country ||
          matchedBox?.ship_destination_country ||
          '',
        [t('ETD Nave')]:
          format(
            addHours(new Date(settlement.ship_trip?.etd.split('T')[0]), 10),
            'dd/MM/yyyy'
          ) || 'N/A',
        [t('ETA Nave')]:
          format(
            addHours(new Date(settlement.ship_trip?.eta.split('T')[0]), 10),
            'dd/MM/yyyy'
          ) || 'N/A',
        [t('POD')]: settlement.ship_trip?.market?.name || t('Sin POD'),
        [t('Fecha Venta')]: format(
          new Date(settlement.date_sold),
          'dd/MM/yyyy'
        ),
        [t('Incoterm Original')]:
          settlement.original_incoterm?.toUpperCase() || 'N/A',
        [t('Contenedor')]: settlement.container_number,
        [t('Mercado Venta')]: settlement.market?.name || t('Sin Mercado'),
        [t('Pallet')]: detail.pallet,
        [t('Variedad')]: detail.variety,
        [t('Bultos')]: detail.quantity,
        [t('Calibre')]: detail.caliber,
        [t('Color')]: detail.color,
        [t('Categoria')]: detail.category,
        [t('Formato')]: detail.format,
        [t('Peso Unitario')]: detail.unit_weight,
        [t('Etiqueta')]: detail.label,
        [t('Kilos')]: detail.kilos,
        [t('Moneda Local')]: detail.local_currency,
        [t('Venta Moneda Local')]: detail.local_total_price,
        [t('Comisión Moneda Local')]: detail.local_total_commision,
        [t('Impuestos Moneda Local')]: detail.local_total_tax,
        [t('Otros Costos Moneda Local')]: detail.local_other_costs,
        [t('Repacking Moneda Local')]: detail.local_repacking_costs,
        [t('CIF Moneda Local')]:
          detail.local_total_price -
          detail.local_total_tax -
          detail.local_total_commision -
          detail.local_other_costs -
          (detail.local_repacking_costs || 0),
        [t('Flete Moneda Local')]: detail.usd_freight * detail.exchange_rate,
        [t('FOB Moneda Local')]: detail.usd_total_fob * detail.exchange_rate,
        [t('Tipo de Cambio')]: detail.exchange_rate,
        [t('Venta USD')]: detail.local_total_price / detail.exchange_rate,
        [t('Comisión USD')]:
          detail.local_total_commision / detail.exchange_rate,
        [t('Impuestos USD')]: detail.local_total_tax / detail.exchange_rate,
        [t('Otros Costos USD')]:
          detail.local_other_costs / detail.exchange_rate,
        [t('Repacking USD')]:
          (detail.local_repacking_costs || 0) / detail.exchange_rate,
        [t('CIF USD')]:
          (detail.local_total_price -
            detail.local_total_tax -
            detail.local_total_commision -
            detail.local_other_costs -
            (detail.local_repacking_costs || 0)) /
          detail.exchange_rate,
        [t('Flete USD')]: detail.usd_freight,
        [t('FOB USD')]: detail.usd_total_fob,
        [t('Diferencia vs Mercado ETA %')]: detail.difference_vs_market_eta
          ? `${detail.difference_vs_market_eta.toFixed(2)}%`
          : 'N/A',
        [t('Diferencia vs Mercado ETA $')]: (() => {
          const diffPercentage = detail.difference_vs_market_eta as
            | number
            | null
            | undefined;
          const localTotalPrice = detail.local_total_price as number | null;
          if (
            typeof diffPercentage !== 'number' ||
            typeof localTotalPrice !== 'number' ||
            !isFinite(diffPercentage) ||
            !isFinite(localTotalPrice) ||
            localTotalPrice === 0
          ) {
            return null;
          }
          const percentage = diffPercentage / 100;
          const denominator = 1 + percentage;
          if (denominator === 0 || !isFinite(denominator)) {
            return null;
          }
          const marketPrice = localTotalPrice / denominator;
          const absoluteDifference = (marketPrice - localTotalPrice) * -1;
          return isFinite(absoluteDifference) ? absoluteDifference : null;
        })(),
      });
    });
  });
  return details;
};

import { REVENUE_TYPE_LABELS as REVENUE_TYPE_EXPORT_LABELS } from '../../../utils/revenue-types';

const collectDistinctRevenueTypes = (settlements: Settlement[]): string[] => {
  const types = new Set<string>();
  settlements.forEach((s) =>
    s.settlement_details.forEach((d) =>
      (d.settlement_detail_revenues || []).forEach((r) => {
        if (r.revenue_type) types.add(r.revenue_type);
      })
    )
  );
  return Array.from(types).sort();
};

const prepareSettlementsDataWithRevenue = (
  settlements: Settlement[],
  t: (key: string) => string
) => {
  const revenueTypes = collectDistinctRevenueTypes(settlements);

  return settlements.map((settlement) => {
    const base = prepareSettlementsData([settlement], t)[0];
    // Sum amount_usd from detail revenues directly (avoids cross-converting with parent exchange rate)
    const revenueAdjUsd = settlement.settlement_details.reduce(
      (sum, d) =>
        sum +
        (d.settlement_detail_revenues || []).reduce(
          (s, r) => s + (r.amount_usd || 0),
          0
        ),
      0
    );
    const revenueAdjLocal = settlement.settlement_details.reduce(
      (sum, d) =>
        sum +
        (d.settlement_detail_revenues || []).reduce(
          (s, r) => s + (r.amount_local || 0),
          0
        ),
      0
    );

    // Per-type breakdown
    const perTypeLocal: Record<string, number> = {};
    const perTypeUsd: Record<string, number> = {};
    revenueTypes.forEach((rt) => {
      perTypeLocal[rt] = 0;
      perTypeUsd[rt] = 0;
    });
    settlement.settlement_details.forEach((d) =>
      (d.settlement_detail_revenues || []).forEach((r) => {
        if (r.revenue_type) {
          perTypeLocal[r.revenue_type] =
            (perTypeLocal[r.revenue_type] || 0) + (r.amount_local || 0);
          perTypeUsd[r.revenue_type] =
            (perTypeUsd[r.revenue_type] || 0) + (r.amount_usd || 0);
        }
      })
    );

    const baseFob = calculateFOBSettlement(settlement);

    const row: Record<string, string | number | null> = {
      ...base,
      [t('Total Otros Ingresos Moneda Local')]: revenueAdjLocal,
    };

    revenueTypes.forEach((rt) => {
      const label = REVENUE_TYPE_EXPORT_LABELS[rt] || rt;
      row[`${t('Otros Ingresos por')} ${label} ${t('Moneda Local')}`] =
        perTypeLocal[rt];
    });

    row[t('Total Otros Ingresos USD')] = revenueAdjUsd;

    revenueTypes.forEach((rt) => {
      const label = REVENUE_TYPE_EXPORT_LABELS[rt] || rt;
      row[`${t('Otros Ingresos por')} ${label} USD`] = perTypeUsd[rt];
    });

    row[t('FOB Ajustado')] = baseFob + revenueAdjUsd;
    row[t('CIF Ajustado')] =
      baseFob + revenueAdjUsd + (settlement.total_freight_usd || 0);

    return row;
  });
};

const prepareSettlementDetailsDataWithRevenue = (
  settlements: Settlement[],
  t: (key: string) => string,
  shipmentBoxes?: ShipmentBox[]
) => {
  const revenueTypes = collectDistinctRevenueTypes(settlements);
  const details: Array<Record<string, string | number | null>> = [];

  settlements.forEach((settlement) => {
    settlement.settlement_details.forEach((detail) => {
      const baseRow = prepareSettlementDetailsData(
        [
          {
            ...settlement,
            settlement_details: [detail],
          },
        ],
        t,
        shipmentBoxes
      )[0];

      // Use actual per-detail revenue amounts (avoids cross-converting with parent exchange rate)
      const revenueLocal = (detail.settlement_detail_revenues || []).reduce(
        (s, r) => s + (r.amount_local || 0),
        0
      );
      const revenueUsd = (detail.settlement_detail_revenues || []).reduce(
        (s, r) => s + (r.amount_usd || 0),
        0
      );

      // Per-type breakdown for this detail
      const perTypeLocal: Record<string, number> = {};
      const perTypeUsd: Record<string, number> = {};
      revenueTypes.forEach((rt) => {
        perTypeLocal[rt] = 0;
        perTypeUsd[rt] = 0;
      });
      (detail.settlement_detail_revenues || []).forEach((r) => {
        if (r.revenue_type) {
          perTypeLocal[r.revenue_type] =
            (perTypeLocal[r.revenue_type] || 0) + (r.amount_local || 0);
          perTypeUsd[r.revenue_type] =
            (perTypeUsd[r.revenue_type] || 0) + (r.amount_usd || 0);
        }
      });

      const adjustedSaleLocal = detail.local_total_price + revenueLocal;
      const cifLocal =
        detail.local_total_price -
        detail.local_total_tax -
        detail.local_total_commision -
        detail.local_other_costs -
        (detail.local_repacking_costs || 0);
      const adjustedCifLocal = cifLocal + revenueLocal;
      const adjustedFobLocal =
        (detail.usd_total_fob + revenueUsd) * (detail.exchange_rate || 1);

      const row: Record<string, string | number | null> = {
        ...baseRow,
        [t('Total Otros Ingresos Moneda Local')]: revenueLocal,
      };

      revenueTypes.forEach((rt) => {
        const label = REVENUE_TYPE_EXPORT_LABELS[rt] || rt;
        row[`${t('Otros Ingresos por')} ${label} ${t('Moneda Local')}`] =
          perTypeLocal[rt];
      });

      row[t('Venta Ajustada Moneda Local')] = adjustedSaleLocal;
      row[t('CIF Ajustado Moneda Local')] = adjustedCifLocal;
      row[t('FOB Ajustado Moneda Local')] = adjustedFobLocal;

      row[t('Total Otros Ingresos USD')] = revenueUsd;

      revenueTypes.forEach((rt) => {
        const label = REVENUE_TYPE_EXPORT_LABELS[rt] || rt;
        row[`${t('Otros Ingresos por')} ${label} USD`] = perTypeUsd[rt];
      });

      row[t('CIF Ajustado USD')] =
        adjustedCifLocal / (detail.exchange_rate || 1);
      row[t('FOB Ajustado USD')] = detail.usd_total_fob + revenueUsd;

      details.push(row);
    });
  });
  return details;
};

const normalizeReceiverName = (value: string | null | undefined) =>
  (value || '').trim().toUpperCase();

const CREATE_RECEIVER_OPTION_KEY = '__create_new_receiver__';

const parseProductKey = (key: string) => {
  const parts = key.split('|');
  return {
    variety: parts[0] || '',
    caliber: parts[1] || '',
    color: parts[2] || '',
    format: parts[3] || '',
    label: parts[4] || '',
    unitWeight: parts[5] || '',
    category: parts[6] || '',
  };
};

const LiquidationsDetails = () => {
  const { t } = useTranslation('settlements');
  const { userProfile, session } = useUserStore();
  const { selectedSeason } = useFilterStore();
  const exporter_id = userProfile?.exporter_id;
  const [, setLocation] = useLocation();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const confirm = useConfirm();
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [exporterReceivers, setExporterReceivers] = useState<
    ExporterReceiver[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedClients, setSelectedClients] = useState<string[]>(['all']);
  const [selectedShips, setSelectedShips] = useState<string[]>(['all']);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTermContainer, setSearchTermContainer] = useState('');
  const [incotermFilter, setIncotermFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({});
  const [settlementViewMode, setSettlementViewMode] = useState<
    'reference' | SettlementSourceViewMode
  >('reference');
  const [sourceSearchTerm, setSourceSearchTerm] = useState('');
  const [sourceDocuments, setSourceDocuments] = useState<
    SourceSettlementDocumentView[]
  >([]);
  const [sourceHierarchyKey, setSourceHierarchyKey] = useState('');
  const [isSourceHierarchyLoading, setIsSourceHierarchyLoading] =
    useState(false);
  const [sourceHierarchyError, setSourceHierarchyError] = useState('');
  const [sourcePdfUrl, setSourcePdfUrl] = useState('');
  const [sourcePdfTitle, setSourcePdfTitle] = useState('');
  const [sourcePdfPage, setSourcePdfPage] = useState(1);
  const [isSourcePdfOpen, setIsSourcePdfOpen] = useState(false);
  const [isSourcePdfLoading, setIsSourcePdfLoading] = useState(false);
  const itemsPerPage = 50;
  const [quadratureFilter, setQuadratureFilter] = useState<string>('all');
  const [sinisterFilter, setSinisterFilter] = useState<string>('all');
  const [showWithRevenues, setShowWithRevenues] = useState(true);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedSettlement, setSelectedSettlement] =
    useState<Settlement | null>(null);
  const [viewedSettlements, setViewedSettlements] = useState<
    Record<string, boolean>
  >({});
  const [selectedWeeks, setSelectedWeeks] = useState<string[]>(['all']);
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([
    'all',
  ]);
  const [showOnlyPendingInfo, setShowOnlyPendingInfo] = useState<string>('all');
  const [showOnlyZeroValues, setShowOnlyZeroValues] = useState(false);
  const [showOnlyKilosMismatch, setShowOnlyKilosMismatch] = useState(false);
  const [expandedCheck, setExpandedCheck] = useState<string | null>(null);
  const [showOnlyNegativeValues, setShowOnlyNegativeValues] = useState(false);
  const [isSinisterModalOpen, setIsSinisterModalOpen] = useState(false);
  const [isBlockedModalOpen, setIsBlockedModalOpen] = useState(false);
  const [isQuadratureInfoOpen, setIsQuadratureInfoOpen] = useState(false);
  const [isUnmappedReceiversOpen, setIsUnmappedReceiversOpen] = useState(false);
  const [receiverSelections, setReceiverSelections] = useState<
    Record<string, string>
  >({});
  const [receiverSearchValues, setReceiverSearchValues] = useState<
    Record<string, string>
  >({});
  const [receiverAutocompleteOpenKeys, setReceiverAutocompleteOpenKeys] =
    useState<Record<string, boolean>>({});
  const [hiddenReceiverAutocompleteKey, setHiddenReceiverAutocompleteKey] =
    useState<string | null>(null);
  const [createReceiverGroup, setCreateReceiverGroup] = useState<{
    key: string;
    receiverName: string;
  } | null>(null);
  const [createReceiverName, setCreateReceiverName] = useState('');
  const [isCreatingReceiver, setIsCreatingReceiver] = useState(false);
  const closingReceiverAutocompleteKeyRef = useRef<string | null>(null);
  const createReceiverTimeoutRef = useRef<ReturnType<
    typeof window.setTimeout
  > | null>(null);
  const [savingReceiverMappings, setSavingReceiverMappings] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [costCategoriesEnabled, setCostCategoriesEnabled] = useState(false);
  // Earlier seasons have no exporter view: hide its export and toggle there.
  const exporterCostViewAvailable =
    costCategoriesEnabled && isExporterCostViewSeason(selectedSeason);
  useEffect(() => {
    if (!exporter_id) return;
    getCustomCostCategoriesEnabled(exporter_id)
      .then(setCostCategoriesEnabled)
      .catch((error) =>
        console.error('Error loading cost categories flag:', error)
      );
  }, [exporter_id]);
  const [sourceCostStandard, setSourceCostStandard] =
    useState<CostStandard>('fm');
  const [sourceExporterCosts, setSourceExporterCosts] = useState<Map<
    string,
    number
  > | null>(null);
  const [downloadingExcelWithRevenue, setDownloadingExcelWithRevenue] =
    useState(false);
  const { selectedSpecies } = useDataStore();
  const [missingDataSettlements, setMissingDataSettlements] = useState<{
    missingExchangeRate: number;
    missingFreight: number;
    totalIncomplete: number;
  }>({ missingExchangeRate: 0, missingFreight: 0, totalIncomplete: 0 });
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [selectedHistorySettlement, setSelectedHistorySettlement] =
    useState<Settlement | null>(null);
  const [historyCounts, setHistoryCounts] = useState<Map<number, number>>(
    new Map()
  );
  const [isExcelUploadOpen, setIsExcelUploadOpen] = useState(false);
  const [isManualUploadOpen, setIsManualUploadOpen] = useState(false);
  const [isShipmentsUploadOpen, setIsShipmentsUploadOpen] = useState(false);
  const [isExcelMassiveUploadOpen, setIsExcelMassiveUploadOpen] =
    useState(false);
  const [isMultiShipmentUploadOpen, setIsMultiShipmentUploadOpen] =
    useState(false);
  const [isMassiveRevenueUploadOpen, setIsMassiveRevenueUploadOpen] =
    useState(false);
  const [isMassiveCostsUploadOpen, setIsMassiveCostsUploadOpen] =
    useState(false);
  const [
    isRecalculatingMarketComparisons,
    setIsRecalculatingMarketComparisons,
  ] = useState(false);
  const {
    shipments,
    loading: shipmentsLoading,
    error: shipmentsError,
  } = useShipmentsStore();
  const { mappedFormats, fetchedMappedAndUnmappedFormats } = useFruticStore();
  const canUseMarketComparisonRecalculate = [
    'vicente.pirozzi@conectikids.cl',
    'demo@cherrymarket.cl',
  ].includes(userProfile?.email ?? '');

  const isQuadratureReady =
    !shipmentsLoading && !shipmentsError && fetchedMappedAndUnmappedFormats;

  const mappedShipments = useMemo(() => {
    if (!shipments.length) return [];

    if (!fetchedMappedAndUnmappedFormats) {
      return shipments.map((box) => ({
        ...box,
        unit_weight: box.unit_weight ?? box.box_weight,
      }));
    }

    return shipments.map((box) => {
      const mapping = mappedFormats.find((m) => m.frutic_code === box.box_type);
      const box_format = mapping?.cherry_box_format || box.box_format || 'NA';
      const unit_weight = box.unit_weight ?? box.box_weight;

      return {
        ...box,
        box_format,
        unit_weight,
      };
    });
  }, [shipments, mappedFormats, fetchedMappedAndUnmappedFormats]);

  // A reference may repeat across divisions; boxes and settlements only match
  // within the same division (reference-only when the division is unknown).
  const quadratureKey = (
    divisionId: number | string | null | undefined,
    reference: string
  ) => `${divisionId ?? ''}::${reference}`;

  const settlementBalanceStatus = useMemo(() => {
    const statusById = new Map<number, 'matched' | 'mismatch' | 'unknown'>();

    if (!isQuadratureReady || !settlements.length) {
      return statusById;
    }

    const shipmentsByReference = new Map<string, typeof mappedShipments>();
    let shipmentBoxesWithoutRef = 0;

    mappedShipments.forEach((box) => {
      const reference = normalizeQuadratureReference(box.reference_number);
      if (!reference) {
        shipmentBoxesWithoutRef += 1;
        return;
      }
      const key = quadratureKey(box.division_id, reference);
      const group = shipmentsByReference.get(key) || [];
      group.push(box);
      shipmentsByReference.set(key, group);
    });

    const shipmentRefsList = Array.from(shipmentsByReference.keys()).sort();
    const settlementRefsList = [
      ...new Set(
        settlements
          .map((s) => {
            const reference = normalizeQuadratureReference(s.reference_number);
            return reference ? quadratureKey(s.division_id, reference) : '';
          })
          .filter(Boolean)
      ),
    ].sort();

    console.log('[Shipment cuadrature] Settlement refs (from settlements):', {
      count: settlementRefsList.length,
      refs: settlementRefsList,
    });
    console.log('[Shipment cuadrature] Shipment refs (from shipment boxes):', {
      count: shipmentRefsList.length,
      refs: shipmentRefsList,
      boxesWithoutRef: shipmentBoxesWithoutRef,
      totalShipmentBoxes: mappedShipments.length,
    });
    const refsOnlyInSettlements = settlementRefsList.filter(
      (r) => !shipmentsByReference.has(r)
    );
    const refsOnlyInShipments = shipmentRefsList.filter(
      (r) => !settlementRefsList.includes(r)
    );
    if (refsOnlyInSettlements.length > 0) {
      console.log(
        '[Shipment cuadrature] Refs that exist in SETTLEMENTS but NOT in SHIPMENTS (no boxes found):',
        refsOnlyInSettlements
      );
    }
    if (refsOnlyInShipments.length > 0) {
      console.log(
        '[Shipment cuadrature] Refs that exist in SHIPMENTS but NOT in SETTLEMENTS:',
        refsOnlyInShipments
      );
    }

    const areQuantitiesEqual = (a: number, b: number) =>
      Math.abs(a - b) < 0.0001;

    settlements.forEach((settlement) => {
      const reference = settlement.reference_number;
      if (!reference) {
        statusById.set(settlement.id, 'unknown');
        console.log(
          '[Shipment cuadrature] Settlement has no reference_number:',
          { id: settlement.id, receiver: settlement.receiver }
        );
        return;
      }

      const shipmentBoxes = shipmentsByReference.get(
        quadratureKey(
          settlement.division_id,
          normalizeQuadratureReference(reference)
        )
      );
      if (!shipmentBoxes || shipmentBoxes.length === 0) {
        statusById.set(settlement.id, 'mismatch');
        console.log(
          '[Shipment cuadrature] No shipment boxes for settlement ref:',
          {
            ref: reference,
            settlementId: settlement.id,
            receiver: settlement.receiver,
            hint: 'Check that shipment data has boxes with this exact reference_number (trim/case sensitive).',
          }
        );
        return;
      }

      const quadratureContext = {
        season: settlement.season,
        speciesId: settlement.species_id,
      };

      const settlementTotals = new Map<string, number>();
      settlement.settlement_details.forEach((detail) => {
        const key = buildSettlementQuadratureProductKey(
          detail,
          quadratureContext
        );
        const current = settlementTotals.get(key) || 0;
        settlementTotals.set(key, current + (detail.quantity || 0));
      });

      const shipmentTotals = new Map<string, number>();
      shipmentBoxes.forEach((box) => {
        const key = buildShipmentQuadratureProductKey(box, quadratureContext);
        const current = shipmentTotals.get(key) || 0;
        shipmentTotals.set(key, current + (box.quantity || 0));
      });

      const keys = new Set([
        ...Array.from(settlementTotals.keys()),
        ...Array.from(shipmentTotals.keys()),
      ]);

      let isMatched = true;
      keys.forEach((key) => {
        const settlementValue = settlementTotals.get(key) || 0;
        const shipmentValue = shipmentTotals.get(key) || 0;
        if (!areQuantitiesEqual(settlementValue, shipmentValue)) {
          isMatched = false;
        }
      });

      if (!isMatched) {
        console.log(
          '[Shipment cuadrature] Quantity mismatch for ref (ref found but totals differ):',
          {
            ref: reference,
            settlementId: settlement.id,
            settlementTotals: Object.fromEntries(settlementTotals),
            shipmentTotals: Object.fromEntries(shipmentTotals),
          }
        );
      }

      statusById.set(settlement.id, isMatched ? 'matched' : 'mismatch');
    });

    return statusById;
  }, [isQuadratureReady, settlements, mappedShipments]);

  const zeroValueSettlements = useMemo(() => {
    const refs = new Set<string>();
    settlements.forEach((s) => {
      const ref = s.reference_number || `ID ${s.id}`;
      const hasZeroQuantity = s.settlement_details?.some(
        (d) => !d.quantity || d.quantity == 0
      );
      const hasZeroWeight = s.settlement_details?.some(
        (d) => !d.unit_weight || d.unit_weight == 0
      );
      if (hasZeroQuantity || hasZeroWeight) {
        refs.add(ref);
      }
    });
    return Array.from(refs);
  }, [settlements]);

  const kilosMismatchSettlements = useMemo(
    () =>
      settlements.filter((settlement) =>
        settlement.settlement_details?.some(hasSettlementKilosMismatch)
      ),
    [settlements]
  );

  const negativeValueSettlements = useMemo(() => {
    return settlements.filter(
      (s) =>
        (s.total_freight_usd != null && s.total_freight_usd < 0) ||
        (s.total_commission_local != null && s.total_commission_local < 0) ||
        (s.total_other_costs_local != null && s.total_other_costs_local < 0) ||
        (s.total_repacking_costs_local != null &&
          s.total_repacking_costs_local < 0) ||
        (s.total_tax_local != null && s.total_tax_local < 0) ||
        (s.total_revenue_local != null && s.total_revenue_local < 0)
    );
  }, [settlements]);

  const quadratureSummary = useMemo(() => {
    const { settlements: filteredSettlements } =
      getFilteredSettlements(settlements);

    let matched = 0;
    let mismatch = 0;
    let unknown = 0;

    filteredSettlements.forEach((settlement) => {
      if (!isQuadratureReady) {
        unknown += 1;
        return;
      }

      const status = settlementBalanceStatus.get(settlement.id);
      if (status === 'matched') {
        matched += 1;
      } else if (status === 'mismatch') {
        mismatch += 1;
      } else {
        unknown += 1;
      }
    });

    return {
      matched,
      mismatch,
      unknown,
      total: filteredSettlements.length,
    };
  }, [
    settlements,
    searchTerm,
    searchTermContainer,
    selectedClients,
    selectedShips,
    incotermFilter,
    selectedWeeks,
    selectedSeason,
    showOnlyPendingInfo,
    quadratureFilter,
    sinisterFilter,
    selectedCurrencies,
    showOnlyZeroValues,
    showOnlyKilosMismatch,
    kilosMismatchSettlements,
    showOnlyNegativeValues,
    negativeValueSettlements,
    isQuadratureReady,
    settlementBalanceStatus,
  ]);

  const getQuadratureDifferences = useMemo(() => {
    if (!isQuadratureReady || !settlements.length) {
      return [];
    }

    const differences: Array<{
      referencia: string;
      cliente: string;
      variedad: string;
      calibre: string;
      color: string;
      formato: string;
      etiqueta: string;
      pesoBulto: string;
      categoria: string;
      liquidado: number;
      despachado: number;
    }> = [];

    const shipmentsByReference = new Map<string, typeof mappedShipments>();
    mappedShipments.forEach((box) => {
      const reference = normalizeQuadratureReference(box.reference_number);
      if (!reference) return;
      const key = quadratureKey(box.division_id, reference);
      const group = shipmentsByReference.get(key) || [];
      group.push(box);
      shipmentsByReference.set(key, group);
    });

    const areQuantitiesEqual = (a: number, b: number) =>
      Math.abs(a - b) < 0.0001;

    settlements.forEach((settlement) => {
      const status = settlementBalanceStatus.get(settlement.id);
      if (status !== 'mismatch') return;

      const reference = settlement.reference_number;
      if (!reference) return;

      const shipmentBoxes =
        shipmentsByReference.get(
          quadratureKey(
            settlement.division_id,
            normalizeQuadratureReference(reference)
          )
        ) || [];
      const quadratureContext = {
        season: settlement.season,
        speciesId: settlement.species_id,
      };

      const settlementTotals = new Map<string, number>();
      settlement.settlement_details.forEach((detail) => {
        const key = buildSettlementQuadratureProductKey(
          detail,
          quadratureContext
        );
        const current = settlementTotals.get(key) || 0;
        settlementTotals.set(key, current + (detail.quantity || 0));
      });

      const shipmentTotals = new Map<string, number>();
      shipmentBoxes.forEach((box) => {
        const key = buildShipmentQuadratureProductKey(box, quadratureContext);
        const current = shipmentTotals.get(key) || 0;
        shipmentTotals.set(key, current + (box.quantity || 0));
      });

      const keys = new Set([
        ...Array.from(settlementTotals.keys()),
        ...Array.from(shipmentTotals.keys()),
      ]);

      keys.forEach((key) => {
        const settlementValue = settlementTotals.get(key) || 0;
        const shipmentValue = shipmentTotals.get(key) || 0;
        if (!areQuantitiesEqual(settlementValue, shipmentValue)) {
          const parsed = parseProductKey(key);
          differences.push({
            referencia: reference,
            cliente: settlement.receiver,
            variedad: parsed.variety,
            calibre: parsed.caliber,
            color: parsed.color,
            formato: parsed.format,
            etiqueta: parsed.label,
            pesoBulto: parsed.unitWeight,
            categoria: parsed.category,
            liquidado: settlementValue,
            despachado: shipmentValue,
          });
        }
      });
    });

    return differences;
  }, [
    isQuadratureReady,
    settlements,
    mappedShipments,
    settlementBalanceStatus,
  ]);

  const exportQuadratureDifferences = () => {
    const differences = getQuadratureDifferences;
    if (differences.length === 0) {
      toast.warning(t('No hay diferencias para exportar'));
      return;
    }

    const wb = XLSX.utils.book_new();
    const data = differences.map((diff) => ({
      [t('Referencia')]: diff.referencia,
      [t('Cliente')]: diff.cliente,
      [t('Variedad')]: diff.variedad,
      [t('Calibre')]: diff.calibre,
      [t('Color')]: diff.color,
      [t('Formato')]: diff.formato,
      [t('Etiqueta')]: diff.etiqueta,
      [t('Peso Unitario')]: diff.pesoBulto,
      [t('Categoría')]: diff.categoria,
      [t('Liquidado')]: diff.liquidado,
      [t('Despachado')]: diff.despachado,
    }));

    const ws = XLSX.utils.json_to_sheet(data);

    // Set column widths
    ws['!cols'] = [
      { wch: 15 }, // Referencia
      { wch: 25 }, // Cliente
      { wch: 15 }, // Variedad
      { wch: 10 }, // Calibre
      { wch: 10 }, // Color
      { wch: 12 }, // Formato
      { wch: 15 }, // Etiqueta
      { wch: 12 }, // Peso Bulto
      { wch: 12 }, // Liquidado
      { wch: 12 }, // Despachado
    ];

    XLSX.utils.book_append_sheet(wb, ws, t('Diferencias Cuadratura'));
    XLSX.writeFile(wb, t('Diferencias_Cuadratura') + '.xlsx');
  };

  const clients = useMemo(() => {
    return Array.from(new Set(settlements.map((s) => s.receiver))).sort();
  }, [settlements]);

  const unmappedReceiverGroups = useMemo(() => {
    const groups = new Map<
      string,
      { key: string; receiverName: string; count: number }
    >();

    settlements.forEach((settlement) => {
      if (settlement.exporter_receiver_id) return;

      const receiverName =
        settlement.legacy_receiver || settlement.receiver || '';
      const key = normalizeReceiverName(receiverName);
      if (!key) return;

      const existing = groups.get(key);
      if (existing) {
        existing.count += 1;
        return;
      }

      groups.set(key, {
        key,
        receiverName,
        count: 1,
      });
    });

    return Array.from(groups.values()).sort((a, b) =>
      a.receiverName.localeCompare(b.receiverName)
    );
  }, [settlements]);

  const handleSaveUnmappedReceiverMappings = async () => {
    if (!exporter_id || unmappedReceiverGroups.length === 0) return;

    const selectedGroups = unmappedReceiverGroups.filter(
      (group) => receiverSelections[group.key]
    );

    if (selectedGroups.length === 0) {
      toast.error(t('Selecciona al menos un cliente oficial.'));
      return;
    }

    setSavingReceiverMappings(true);
    try {
      const results = await Promise.all(
        selectedGroups.map((group) =>
          setSettlementReceiverForLegacyName({
            exporterId: exporter_id,
            legacyReceiverName: group.receiverName,
            exporterReceiverId: Number(receiverSelections[group.key]),
          })
        )
      );

      if (results.some((result) => !result)) {
        throw new Error('Some receiver mappings failed');
      }

      toast.success(t('Clientes asociados correctamente.'));
      setIsUnmappedReceiversOpen(false);
      setReceiverSelections({});
      setReceiverSearchValues({});
      setReceiverAutocompleteOpenKeys({});
      setHiddenReceiverAutocompleteKey(null);
      await fetchSettlements();
    } catch (error) {
      console.error('Error mapping settlement receivers:', error);
      toast.error(t('Error al asociar clientes.'));
    } finally {
      setSavingReceiverMappings(false);
    }
  };

  const openCreateReceiverModal = (
    group: (typeof unmappedReceiverGroups)[number]
  ) => {
    const prefilledName = (
      receiverSearchValues[group.key] || group.receiverName
    ).trim();
    closingReceiverAutocompleteKeyRef.current = group.key;
    setHiddenReceiverAutocompleteKey(group.key);
    setReceiverAutocompleteOpenKeys((prev) => ({
      ...prev,
      [group.key]: false,
    }));
    setCreateReceiverName(prefilledName);

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    if (createReceiverTimeoutRef.current) {
      window.clearTimeout(createReceiverTimeoutRef.current);
    }

    createReceiverTimeoutRef.current = window.setTimeout(() => {
      setCreateReceiverGroup({
        key: group.key,
        receiverName: group.receiverName,
      });
    }, 50);
  };

  const closeCreateReceiverModal = () => {
    closingReceiverAutocompleteKeyRef.current = null;
    setHiddenReceiverAutocompleteKey(null);
    if (createReceiverTimeoutRef.current) {
      window.clearTimeout(createReceiverTimeoutRef.current);
      createReceiverTimeoutRef.current = null;
    }
    setCreateReceiverGroup(null);
    setCreateReceiverName('');
    setIsCreatingReceiver(false);
  };

  const handleCreateReceiver = async () => {
    if (!exporter_id || !createReceiverGroup) return;

    const receiverName = createReceiverName.trim();
    if (!receiverName) {
      toast.error(t('Ingresa un nombre de cliente'));
      return;
    }

    const existingReceiver = exporterReceivers.find(
      (receiver) =>
        normalizeReceiverName(receiver.name) ===
        normalizeReceiverName(receiverName)
    );

    if (existingReceiver) {
      setReceiverSelections((prev) => ({
        ...prev,
        [createReceiverGroup.key]: existingReceiver.id.toString(),
      }));
      closeCreateReceiverModal();
      toast.info(t('El cliente ya existe y fue seleccionado'));
      return;
    }

    setIsCreatingReceiver(true);
    try {
      const createdReceiver = await addReceiverToExporter(
        exporter_id,
        receiverName
      );

      setExporterReceivers((prev) =>
        [...prev, createdReceiver].sort((a, b) => a.name.localeCompare(b.name))
      );
      setReceiverSelections((prev) => ({
        ...prev,
        [createReceiverGroup.key]: createdReceiver.id.toString(),
      }));
      closeCreateReceiverModal();
      toast.success(t('Cliente creado y seleccionado'));
    } catch (error) {
      console.error('Error creating receiver:', error);
      toast.error(t('Error al crear cliente'));
    } finally {
      setIsCreatingReceiver(false);
    }
  };

  const ships = useMemo(() => {
    return Array.from(
      new Set(settlements.map((s) => s.ship_trip?.ship?.name))
    ).sort();
  }, [settlements]);

  const currencies = useMemo(() => {
    const currencySet = new Set<string>();
    settlements.forEach((s) => {
      if (s.local_currency) currencySet.add(s.local_currency);
    });
    return Array.from(currencySet).sort();
  }, [settlements]);

  function getFilteredSettlements(settlements: Settlement[]) {
    const filtered = settlements.filter((settlement) => {
      const clientMatch =
        selectedClients.includes('all') ||
        selectedClients.includes(settlement.receiver);
      const shipMatch =
        selectedShips.includes('all') ||
        selectedShips.includes(settlement?.ship_trip?.ship?.name);
      const searchMatch = searchTerm
        ? settlement.reference_number
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
        : true;
      const searchMatchContainer = searchTermContainer
        ? settlement.container_number
            ?.toLowerCase()
            .includes(searchTermContainer.toLowerCase())
        : true;
      const incotermMatch =
        incotermFilter === 'all' ||
        settlement.original_incoterm === incotermFilter;
      const seasonMatch =
        selectedSeason === 'all' || settlement.season === selectedSeason;

      // Add week filtering
      const weekNumber = getISOWeek(new Date(settlement.date_sold));
      const weekMatch =
        selectedWeeks.includes('all') ||
        selectedWeeks.includes(weekNumber.toString());

      // Modify pending info filter to use select values
      const pendingInfoMatch =
        showOnlyPendingInfo === 'all'
          ? true
          : showOnlyPendingInfo === 'pending'
            ? getSettlementIncoterm(settlement) !== 'fob'
            : getSettlementIncoterm(settlement) === 'fob';

      const quadratureMatch =
        quadratureFilter === 'all' || !isQuadratureReady
          ? true
          : settlementBalanceStatus.get(settlement.id) ===
            (quadratureFilter === 'mismatch' ? 'mismatch' : 'matched');

      const currencyMatch =
        selectedCurrencies.includes('all') ||
        selectedCurrencies.includes(settlement.local_currency);

      const sinisterMatch =
        sinisterFilter === 'all' ||
        (sinisterFilter === 'only_sinister' && settlement.is_sinister) ||
        (sinisterFilter === 'no_sinister' && !settlement.is_sinister);

      const zeroValueMatch =
        !showOnlyZeroValues ||
        (() => {
          const ref = settlement.reference_number || `ID ${settlement.id}`;
          return zeroValueSettlements.includes(ref);
        })();

      const kilosMatch =
        !showOnlyKilosMismatch ||
        kilosMismatchSettlements.some((s) => s.id === settlement.id);

      const negativeValueMatch =
        !showOnlyNegativeValues ||
        negativeValueSettlements.some((s) => s.id === settlement.id);

      return (
        clientMatch &&
        shipMatch &&
        searchMatch &&
        searchMatchContainer &&
        incotermMatch &&
        weekMatch &&
        seasonMatch &&
        pendingInfoMatch &&
        quadratureMatch &&
        currencyMatch &&
        sinisterMatch &&
        zeroValueMatch &&
        kilosMatch &&
        negativeValueMatch
      );
    });

    // Si hay un término de búsqueda y solo una coincidencia
    if ((searchTerm || searchTermContainer) && filtered.length === 1) {
      const weekStart = getWeekStart(new Date(filtered[0].date_sold));
      const weekKey = weekStart.toISOString().split('T')[0];
      return { settlements: filtered, singleMatchWeek: weekKey };
    }

    return { settlements: filtered, singleMatchWeek: null };
  }

  const fetchSettlements = async () => {
    setIsLoading(true);

    try {
      const data = await getSettlementDetailsPageRPC(
        exporter_id,
        selectedSeason,
        selectedSpecies?.id!,
        showWithRevenues
      );

      if (data) {
        setSettlements(data);
        setCurrentPage(1);
        const settlementIds = data.map((settlement) => settlement.id);
        const countsMap = await getSettlementHistoryCounts(settlementIds);
        setHistoryCounts(countsMap);
      }
    } catch (error) {
      console.error('Error fetching settlements:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const sourceContextKey = `${exporter_id || ''}:${selectedSeason || ''}:${selectedSpecies?.id || ''}`;

  const hasSourceGroups = useMemo(
    () =>
      sourceHierarchyKey === sourceContextKey &&
      sourceDocuments.some((document) => document.groups.length > 0),
    [sourceContextKey, sourceDocuments, sourceHierarchyKey]
  );

  const fetchSourceHierarchy = useCallback(async () => {
    if (!exporter_id || !selectedSpecies?.id) return;
    setIsSourceHierarchyLoading(true);
    setSourceHierarchyError('');
    try {
      const data = await getSettlementSourceHierarchyRPC(
        exporter_id,
        selectedSeason,
        selectedSpecies.id
      );
      if (data === null) {
        throw new Error(t('No se pudo cargar la trazabilidad de origen'));
      }
      setSourceDocuments(data);
      setSourceHierarchyKey(sourceContextKey);
    } catch (error) {
      console.error('Error fetching settlement source hierarchy:', error);
      setSourceHierarchyError(
        error instanceof Error
          ? error.message
          : t('No se pudo cargar la trazabilidad de origen')
      );
    } finally {
      setIsSourceHierarchyLoading(false);
    }
  }, [exporter_id, selectedSeason, selectedSpecies?.id, sourceContextKey, t]);

  useEffect(() => {
    if (sourceHierarchyKey === sourceContextKey) {
      return;
    }
    void fetchSourceHierarchy();
  }, [fetchSourceHierarchy, sourceContextKey, sourceHierarchyKey]);

  useEffect(() => {
    setSourceExporterCosts(null);
    if (sourceCostStandard !== 'exporter') return;
    const settlementIds = Array.from(
      new Set(
        sourceDocuments.flatMap((document) =>
          document.groups.flatMap((group) =>
            group.references.map((reference) => reference.settlementId)
          )
        )
      )
    );
    let cancelled = false;
    getSettlementSourceExporterCosts(settlementIds)
      .then((totals) => {
        if (!cancelled) setSourceExporterCosts(totals);
      })
      .catch((error) => {
        console.error('Error loading exporter source costs:', error);
        toast.error(t('No se pudieron cargar los costos estándar exportadora'));
      });
    return () => {
      cancelled = true;
    };
  }, [sourceCostStandard, sourceDocuments, t]);

  // In the exporter view, references outside it (earlier seasons) keep FM.
  const displayedSourceDocuments = useMemo(() => {
    if (sourceCostStandard !== 'exporter' || !sourceExporterCosts) {
      return sourceDocuments;
    }
    return sourceDocuments.map((document) => ({
      ...document,
      groups: document.groups.map((group) => {
        const references = group.references.map((reference) => {
          const totalCosts = sourceExporterCosts.get(
            `${group.id}:${reference.settlementId}`
          );
          return totalCosts === undefined
            ? reference
            : { ...reference, totalCosts, net: reference.sales - totalCosts };
        });
        const moneyByCurrency = new Map<
          string,
          (typeof group.moneyTotals)[number]
        >();
        references.forEach((reference) => {
          const current = moneyByCurrency.get(reference.currency) || {
            currency: reference.currency,
            sales: 0,
            tax: 0,
            commission: 0,
            freight: 0,
            repacking: 0,
            otherCosts: 0,
            totalCosts: 0,
            net: 0,
          };
          current.sales += reference.sales;
          current.totalCosts += reference.totalCosts;
          current.net += reference.net;
          moneyByCurrency.set(reference.currency, current);
        });
        return {
          ...group,
          references,
          moneyTotals: Array.from(moneyByCurrency.values()),
        };
      }),
    }));
  }, [sourceCostStandard, sourceDocuments, sourceExporterCosts]);

  useEffect(() => {
    if (
      sourceHierarchyKey === sourceContextKey &&
      !hasSourceGroups &&
      settlementViewMode !== 'reference'
    ) {
      setSettlementViewMode('reference');
      setSourceSearchTerm('');
    }
  }, [
    hasSourceGroups,
    settlementViewMode,
    sourceContextKey,
    sourceHierarchyKey,
  ]);

  const openSourcePdf = useCallback(
    async (
      bucketId: string,
      storagePath: string,
      fileName: string,
      pageNumber: number
    ) => {
      setIsSourcePdfLoading(true);
      setSourcePdfTitle(fileName);
      setSourcePdfPage(pageNumber);
      setIsSourcePdfOpen(true);
      try {
        const { data, error } = await supabase.storage
          .from(bucketId)
          .createSignedUrl(storagePath, 60 * 60);
        if (error || !data?.signedUrl) {
          throw error || new Error(t('No se pudo abrir el PDF original'));
        }
        setSourcePdfUrl(data.signedUrl);
      } catch (error) {
        console.error('Error opening source PDF:', error);
        toast.error(t('No se pudo abrir el PDF original'));
        setIsSourcePdfOpen(false);
      } finally {
        setIsSourcePdfLoading(false);
      }
    },
    [t]
  );

  const reloadHistoryCounts = async () => {
    const settlementIds = settlements.map((settlement) => settlement.id);
    if (settlementIds.length === 0) {
      setHistoryCounts(new Map());
      return;
    }

    const countsMap = await getSettlementHistoryCounts(settlementIds);
    setHistoryCounts(countsMap);
  };

  useEffect(() => {
    return () => {
      if (createReceiverTimeoutRef.current) {
        window.clearTimeout(createReceiverTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!exporter_id) return;

    getReceiversByExporter(exporter_id)
      .then((receivers) => setExporterReceivers(receivers || []))
      .catch((error) => {
        console.error('Error fetching exporter receivers:', error);
        toast.error(t('Error al cargar clientes oficiales.'));
      });
  }, [exporter_id, t]);

  const recalculateAllMarketComparisons = async () => {
    if (!canUseMarketComparisonRecalculate || !session?.access_token) return;
    if (!exporter_id || !selectedSeason || !selectedSpecies?.id) {
      toast.error(t('Faltan filtros para recalcular'));
      return;
    }

    const confirmed = await confirm({
      title: t('¿Recalcular la comparación de mercado?'),
      message: t('Se recalculará para todas las liquidaciones de {{season}}.', {
        season: selectedSeason,
      }),
      confirmLabel: t('Recalcular'),
    });
    if (!confirmed) return;

    setIsRecalculatingMarketComparisons(true);
    try {
      const data = await getSettlementDetailsPageRPC(
        exporter_id,
        selectedSeason,
        selectedSpecies.id
      );
      const settlementsToRecalculate = data || [];

      if (settlementsToRecalculate.length === 0) {
        toast.info(t('No hay liquidaciones para recalcular'));
        return;
      }

      const toastId = toast.loading(
        `Recalculando 0/${settlementsToRecalculate.length} liquidaciones...`
      );
      let processed = 0;
      let failed = 0;
      const batchSize = 5;

      for (let i = 0; i < settlementsToRecalculate.length; i += batchSize) {
        const batch = settlementsToRecalculate.slice(i, i + batchSize);
        const results = await Promise.allSettled(
          batch.map((settlement) =>
            fetch(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calculate-settlement-market-comparison`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${session.access_token}`,
                },
                body: JSON.stringify({
                  settlement_id: settlement.id,
                  modifyMarketDateSold: true,
                  modifyMarketEta: true,
                }),
              }
            ).then(async (response) => {
              if (!response.ok) {
                throw new Error(await response.text());
              }
              return response.json();
            })
          )
        );

        processed += batch.length;
        failed += results.filter(
          (result) => result.status === 'rejected'
        ).length;
        toast.update(toastId, {
          render: `Recalculando ${processed}/${settlementsToRecalculate.length} liquidaciones...`,
          isLoading: true,
        });
      }

      toast.update(toastId, {
        render:
          failed > 0
            ? `Recálculo terminado con ${failed} errores`
            : 'Recálculo de mercado completado',
        type: failed > 0 ? 'warning' : 'success',
        isLoading: false,
        autoClose: 5000,
      });

      await fetchSettlements();
    } catch (error) {
      console.error('Error recalculating market comparisons:', error);
      toast.error(t('Error al recalcular el mercado'));
    } finally {
      setIsRecalculatingMarketComparisons(false);
    }
  };

  useEffect(() => {
    console.log('shipments', shipments);
  }, [shipments]);

  const fetchUserActions = async () => {
    if (exporter_id) {
      const actions = await getUserActions({
        entityId: 'settlements-details-id',
      });

      // Create a map of settlement ID + incoterm to viewed status
      const viewedMap = actions.reduce(
        (acc: Record<string, boolean>, action) => {
          if (action.details?.id && action.details?.incoterm) {
            const key = `${action.details.id}-${action.details.incoterm}`;
            acc[key] = true;
          }
          return acc;
        },
        {}
      );

      setViewedSettlements(viewedMap);
    }
  };

  useEffect(() => {
    fetchSettlements();
    fetchUserActions();
  }, [exporter_id, selectedSpecies, selectedSeason]);

  // Re-fetch with revenues when toggle is enabled
  useEffect(() => {
    if (!showWithRevenues || !selectedSpecies || settlements.length === 0)
      return;

    const hasExtraRevenue = settlements.some(
      (s) => (s.total_other_revenue_local || 0) !== 0
    );
    if (!hasExtraRevenue) return;

    const hasLoadedRevenueDetails = settlements.some((s) =>
      s.settlement_details.some(
        (d) => (d.settlement_detail_revenues || []).length > 0
      )
    );
    if (hasLoadedRevenueDetails) return;

    let cancelled = false;

    const fetchWithRevenues = async () => {
      const data = await getSettlementDetailsPageRPC(
        exporter_id,
        selectedSeason,
        selectedSpecies?.id!,
        true
      );

      if (cancelled || !data) return;
      setSettlements(data);
    };

    fetchWithRevenues();

    return () => {
      cancelled = true;
    };
  }, [
    showWithRevenues,
    selectedSpecies,
    settlements,
    exporter_id,
    selectedSeason,
  ]);

  useEffect(() => {
    logUserAction('view', 'page', 'settlements-details');
  }, []);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    searchTermContainer,
    selectedClients,
    selectedShips,
    incotermFilter,
    selectedWeeks,
    selectedSeason,
    showOnlyPendingInfo,
    quadratureFilter,
    sinisterFilter,
    selectedCurrencies,
    showOnlyZeroValues,
    showOnlyKilosMismatch,
    kilosMismatchSettlements,
    showOnlyNegativeValues,
    negativeValueSettlements,
  ]);

  const columns = [
    { name: t('REF'), uid: 'reference_number', sortable: true },
    { name: t('ACCIONES'), uid: 'actions', sortable: false },
    { name: t('VISTA'), uid: 'viewed', sortable: true },
    { name: t('DESPACHO'), uid: 'squared', sortable: true },
    { name: t('SEMANA'), uid: 'week', sortable: true },
    { name: t('CLIENTE'), uid: 'receiver', sortable: true },
    { name: t('NAVE'), uid: 'ship_trip_id', sortable: true },
    { name: t('POD'), uid: 'pod', sortable: true },
    { name: t('CONTENEDOR'), uid: 'container_number', sortable: true },
    { name: t('INC.'), uid: 'original_incoterm', sortable: true },
    { name: t('CIF'), uid: 'cif', sortable: true },
    { name: t('FOB'), uid: 'fob', sortable: true },
    { name: t('VS MERCADO'), uid: 'difference_vs_market', sortable: true },
    { name: t('DESEMPEÑO'), uid: 'status', sortable: true },
    { name: t('INFO'), uid: 'infoStatus', sortable: true },
  ];

  const renderCell = (settlement: Settlement, columnKey: string) => {
    switch (columnKey) {
      case 'date_sold':
        return (
          <div>{format(new Date(settlement.date_sold), 'dd/MM/yyyy')}</div>
        );

      case 'pod':
        return <div>{settlement.ship_trip?.market?.name}</div>;
      case 'week': {
        const weekNumber = getISOWeek(new Date(settlement.date_sold));
        return <div>{weekNumber}</div>;
      }
      case 'kilos':
        return (
          <div>
            {formatNumber(
              settlement.settlement_details.reduce(
                (sum, detail) => sum + (detail.kilos || 0),
                0
              )
            )}{' '}
            kg
          </div>
        );

      case 'cif': {
        const fob = calculateFOBSettlement(settlement);
        // Sum amount_usd from detail revenues directly (avoids cross-converting with parent exchange rate)
        const revenueAdj = settlement.settlement_details.reduce(
          (sum, d) =>
            (d.settlement_detail_revenues || []).reduce(
              (s, r) => s + (r.amount_usd || 0),
              sum
            ),
          0
        );
        const baseCif = fob + (settlement.total_freight_usd || 0);
        const adjustedCif = baseCif + revenueAdj;
        return (
          <div className='flex flex-col'>
            {showWithRevenues && revenueAdj !== 0 ? (
              <>
                <p className='line-through text-default-300 text-xs'>
                  {formatMoneyUSD(baseCif, { decimals: 0 })}
                </p>
                <p className='text-bold text-small text-green-600 dark:text-green-400'>
                  {formatMoneyUSD(adjustedCif, { decimals: 0 })}
                </p>
              </>
            ) : (
              <p className='text-bold text-small'>
                {formatMoneyUSD(baseCif, { decimals: 0 })}
              </p>
            )}
          </div>
        );
      }
      case 'fob': {
        const baseFob = calculateFOBSettlement(settlement);
        // Sum amount_usd from detail revenues directly (avoids cross-converting with parent exchange rate)
        const revenueAdj = settlement.settlement_details.reduce(
          (sum, d) =>
            (d.settlement_detail_revenues || []).reduce(
              (s, r) => s + (r.amount_usd || 0),
              sum
            ),
          0
        );
        return (
          <div className='flex flex-col'>
            {settlement.total_freight_usd ? (
              showWithRevenues && revenueAdj !== 0 ? (
                <>
                  <p className='line-through text-default-300 text-xs'>
                    {formatMoneyUSD(baseFob, { decimals: 0 })}
                  </p>
                  <p className='text-bold text-small text-green-600 dark:text-green-400'>
                    {formatMoneyUSD(baseFob + revenueAdj, { decimals: 0 })}
                  </p>
                </>
              ) : (
                <p className='text-bold text-small'>
                  {formatMoneyUSD(baseFob, { decimals: 0 })}
                </p>
              )
            ) : (
              <p className='text-bold text-small'>-</p>
            )}
          </div>
        );
      }
      case 'original_incoterm':
        return <div>{settlement.original_incoterm}</div>;
      case 'status': {
        const quartileGroups = settlement.settlement_details?.reduce(
          (acc, detail) => {
            const quart = detail.quartil_vs_market_eta;
            const kilos = detail.kilos || 0;
            if (quart === null || quart === undefined) {
              acc['null'] = (acc['null'] || 0) + kilos;
            } else {
              acc[quart] = (acc[quart] || 0) + kilos;
            }
            return acc;
          },
          {} as Record<string | number, number>
        );
        const total =
          Object.values(quartileGroups || {}).reduce((s, v) => s + v, 0) || 0;
        const perf = {
          q1: Math.round(
            (((quartileGroups?.[1] || 0) as number) / (total || 1)) * 100
          ),
          q2: Math.round(
            (((quartileGroups?.[2] || 0) as number) / (total || 1)) * 100
          ),
          q3: Math.round(
            (((quartileGroups?.[3] || 0) as number) / (total || 1)) * 100
          ),
          q4: Math.round(
            (((quartileGroups?.[4] || 0) as number) / (total || 1)) * 100
          ),
          nulls: Math.round(
            (((quartileGroups?.['null'] || 0) as number) / (total || 1)) * 100
          ),
        };
        const segments = [
          {
            label: t('N/C'),
            value: perf.nulls,
            bg: 'bg-default-400',
          },
          { label: 'Q1', value: perf.q1, bg: 'bg-red-500 dark:bg-red-600' },
          {
            label: 'Q2',
            value: perf.q2,
            bg: 'bg-yellow-500 dark:bg-yellow-600',
          },
          { label: 'Q3', value: perf.q3, bg: 'bg-green-500 dark:bg-green-600' },
          { label: 'Q4', value: perf.q4, bg: 'bg-blue-500 dark:bg-blue-600' },
        ];

        return (
          <Tooltip
            content={
              <div className='p-1 text-xs space-y-1'>
                {perf.nulls > 0 && (
                  <div className='flex items-center gap-2'>
                    <span className='inline-flex h-2 w-2 rounded-sm bg-default-400' />
                    <span>
                      {t('No comparable')}: {perf.nulls}%
                    </span>
                  </div>
                )}
                <div className='flex items-center gap-2'>
                  <span className='inline-flex h-2 w-2 rounded-sm bg-red-500' />
                  <span>Q1: {perf.q1}%</span>
                </div>
                <div className='flex items-center gap-2'>
                  <span className='inline-flex h-2 w-2 rounded-sm bg-yellow-500' />
                  <span>Q2: {perf.q2}%</span>
                </div>
                <div className='flex items-center gap-2'>
                  <span className='inline-flex h-2 w-2 rounded-sm bg-green-500' />
                  <span>Q3: {perf.q3}%</span>
                </div>
                <div className='flex items-center gap-2'>
                  <span className='inline-flex h-2 w-2 rounded-sm bg-blue-500' />
                  <span>Q4: {perf.q4}%</span>
                </div>
              </div>
            }
          >
            <div className='flex items-center gap-0.5'>
              {segments.map((s) => (
                <span
                  key={s.label}
                  className={`${s.value > 0 ? s.bg + ' text-white' : 'bg-default-100 dark:bg-default-50 text-default-300 dark:text-default-500'} text-xs font-semibold leading-none rounded px-1.5 py-1 min-w-[22px] text-center`}
                >
                  {s.value}
                </span>
              ))}
            </div>
          </Tooltip>
        );
      }
      case 'exchange_rate':
        return <div>{settlement.exchange_rate}</div>;
      case 'total_freight_usd':
        return (
          <div>
            {settlement.total_freight_usd
              ? formatMoneyUSD(settlement.total_freight_usd, { decimals: 0 })
              : 'N/A'}
          </div>
        );
      case 'difference_vs_market': {
        const detailsWithMarketDiff = settlement.settlement_details.filter(
          (detail) => detail.difference_vs_market_eta !== null
        );

        const totalKilosWithMarketDiff = detailsWithMarketDiff.reduce(
          (sum, detail) => sum + (detail.kilos || 0),
          0
        );

        const totalKilos = settlement.settlement_details.reduce(
          (sum, detail) => sum + (detail.kilos || 0),
          0
        );

        const avgDifference = calculateWeightedAverage(
          settlement.settlement_details.map((detail) => ({
            kilos: detail.kilos,
            value: detail.difference_vs_market_eta,
          })),
          (item) => item.value
        );

        const comparablePercentage =
          (totalKilosWithMarketDiff / totalKilos) * 100;

        return (
          <Tooltip
            placement='left'
            content={
              <div className='p-2'>
                <p className='text-sm font-semibold mb-1'>
                  {t('Kilos comparables vs mercado')}:
                </p>
                <p className='text-sm'>
                  {totalKilosWithMarketDiff.toLocaleString('es-ES')} kg /{' '}
                  {totalKilos.toLocaleString('es-ES')} kg
                </p>
              </div>
            }
          >
            <div className='flex flex-col'>
              <div
                className={
                  avgDifference > 0
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                }
              >
                {formatNumber(avgDifference, {
                  decimals: 2,
                })}
                %
              </div>
            </div>
          </Tooltip>
        );
      }
      case 'squared': {
        if (!isQuadratureReady && !shipmentsError) {
          return <Spinner size='sm' />;
        }
        const status = settlementBalanceStatus.get(settlement.id);

        if (!status) {
          return (
            <Tooltip content={t('N/A')}>
              <span>
                <Icon icon='tabler:minus' className='text-default-300' />
              </span>
            </Tooltip>
          );
        }

        return (
          <Tooltip
            content={status === 'matched' ? t('Cuadrada') : t('No cuadrada')}
          >
            <span>
              {status === 'matched' ? (
                <Icon icon='tabler:check' className='text-success' />
              ) : (
                <Icon icon='tabler:x' className='text-danger' />
              )}
            </span>
          </Tooltip>
        );
      }
      case 'ship_trip_id':
        return (
          <div className='max-w-[80px]'>
            {settlement.ship_trip?.ship?.name || t('Sin embarque')}
          </div>
        );
      case 'infoStatus': {
        const status = getSettlementIncoterm(settlement);
        const statusConfig = {
          pre_liquidacion: {
            label: t('Preliquidación'),
            icon: <Icon icon='tabler:clock' className='text-warning' />,
          },
          cif: {
            label: t('Falta Info'),
            icon: <Icon icon='tabler:info-circle' className='text-primary' />,
          },
          fob: {
            label: 'OK',
            icon: <Icon icon='tabler:check' className='text-success' />,
          },
        };

        return (
          <Tooltip content={statusConfig[status].label}>
            <span>{statusConfig[status].icon}</span>
          </Tooltip>
        );
      }
      case 'viewed': {
        const incoterm = getSettlementIncoterm(settlement);
        const viewKey = `${settlement.id}-${incoterm}`;
        const isViewed = viewedSettlements[viewKey];
        return (
          <Tooltip content={isViewed ? t('Vista') : t('No Vista')}>
            <span>
              {isViewed ? (
                <Icon icon='tabler:check' className='text-success' />
              ) : (
                <Icon icon='tabler:minus' className='text-default-300' />
              )}
            </span>
          </Tooltip>
        );
      }
      case 'actions':
        return (
          <div className='flex items-center gap-0.5'>
            <Tooltip content={t('Ver Reporte')}>
              <Button
                isIconOnly
                size='sm'
                variant='light'
                onClick={() =>
                  setLocation(`/settlements/details/${settlement.id}`)
                }
              >
                <Icon icon='tabler:eye' className='text-default-400' />
              </Button>
            </Tooltip>
            <Tooltip content={t('Editar')}>
              <Button
                isIconOnly
                size='sm'
                variant='light'
                onPress={async () => {
                  const detailedSettlement = await getSettlementById(
                    settlement.id
                  );
                  if (detailedSettlement) {
                    setSelectedSettlement(detailedSettlement);
                    setIsEditOpen(true);
                  } else {
                    toast.error(
                      t('Error al cargar los detalles de la liquidación')
                    );
                  }
                }}
              >
                <Icon icon='tabler:edit' className='text-default-400' />
              </Button>
            </Tooltip>
            <Tooltip content={t('Ver Bitácora')}>
              <div className='relative'>
                <Button
                  isIconOnly
                  size='sm'
                  variant='light'
                  onPress={() => {
                    setSelectedHistorySettlement(settlement);
                    setIsHistoryOpen(true);
                  }}
                >
                  <Icon icon='tabler:book' className='text-default-400' />
                </Button>
                {(historyCounts.get(settlement.id) || 0) > 0 && (
                  <span className='absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-white shadow-sm'>
                    {(historyCounts.get(settlement.id) || 0) > 99
                      ? '99+'
                      : historyCounts.get(settlement.id)}
                  </span>
                )}
              </div>
            </Tooltip>
            <Tooltip
              content={
                settlement.is_blocked
                  ? t('Liquidación Bloqueada')
                  : t('Bloquear Liquidación')
              }
            >
              <Button
                isIconOnly
                size='sm'
                variant='light'
                onPress={() => {
                  setSelectedSettlement(settlement);
                  setIsBlockedModalOpen(true);
                }}
              >
                <Icon icon='tabler:lock'
                  className={
                    settlement.is_blocked ? 'text-danger' : 'text-default-400'
                  }
                />
              </Button>
            </Tooltip>
            <Tooltip
              content={
                settlement.is_sinister
                  ? t('Liquidación Siniestrada')
                  : t('Marcar como Siniestro')
              }
            >
              <Button
                isIconOnly
                size='sm'
                variant='light'
                onPress={() => {
                  setSelectedSettlement(settlement);
                  setIsSinisterModalOpen(true);
                }}
              >
                <Icon icon='tabler:flag'
                  className={
                    settlement.is_sinister ? 'text-danger' : 'text-default-400'
                  }
                />
              </Button>
            </Tooltip>
          </div>
        );
      case 'container_number':
        return (
          <div className='flex items-center gap-2'>
            <span>{settlement.container_number || 'N/A'}</span>
            {/* {settlement.container_number} */}
            {/* && (
              <ContainerTrackingSyncButton
                containerNumber={settlement.container_number}
                settlementId={settlement.id}
                onSyncComplete={() => fetchSettlements()}
                size='sm'
                showLabel={false}
              />
            )} */}
          </div>
        );
      default:
        return settlement[columnKey as keyof Settlement];
    }
  };
  const getSortValue = useCallback(
    (settlement: Settlement, columnKey: string): string | number => {
      switch (columnKey) {
        case 'reference_number':
          return settlement.reference_number || '';
        case 'week':
          return getISOWeek(new Date(settlement.date_sold));
        case 'receiver':
          return settlement.receiver || '';
        case 'ship_trip_id':
          return settlement.ship_trip?.ship?.name || '';
        case 'pod':
          return settlement.ship_trip?.market?.name || '';
        case 'container_number':
          return settlement.container_number || '';
        case 'original_incoterm':
          return settlement.original_incoterm || '';
        case 'cif': {
          const fob = calculateFOBSettlement(settlement);
          return fob + (settlement.total_freight_usd || 0);
        }
        case 'fob':
          return calculateFOBSettlement(settlement);
        case 'difference_vs_market':
          return calculateWeightedAverage(
            settlement.settlement_details.map((detail) => ({
              kilos: detail.kilos,
              value: detail.difference_vs_market_eta,
            })),
            (item) => item.value
          );
        case 'viewed': {
          const incoterm = getSettlementIncoterm(settlement);
          const viewKey = `${settlement.id}-${incoterm}`;
          return viewedSettlements[viewKey] ? 1 : 0;
        }
        case 'squared': {
          const status = settlementBalanceStatus.get(settlement.id);
          if (!status) return -1;
          return status === 'matched' ? 1 : 0;
        }
        case 'infoStatus': {
          const status = getSettlementIncoterm(settlement);
          if (status === 'fob') return 2; // OK
          if (status === 'cif') return 1; // Falta Info
          return 0; // Preliquidación
        }
        case 'status': {
          // Score = weighted sum favoring higher quartiles (Q4 best, Q1 worst)
          // Non-comparable kilos are excluded from the calculation
          const quartileGroups = settlement.settlement_details?.reduce(
            (acc, detail) => {
              const quart = detail.quartil_vs_market_eta;
              const kilos = detail.kilos || 0;
              if (quart !== null && quart !== undefined) {
                acc[quart] = (acc[quart] || 0) + kilos;
              }
              return acc;
            },
            {} as Record<number, number>
          );
          const comparableTotal =
            Object.values(quartileGroups || {}).reduce((s, v) => s + v, 0) || 1;
          // Weighted score: Q4=4, Q3=3, Q2=2, Q1=1
          const score =
            ((quartileGroups?.[4] || 0) * 4 +
              (quartileGroups?.[3] || 0) * 3 +
              (quartileGroups?.[2] || 0) * 2 +
              (quartileGroups?.[1] || 0) * 1) /
            comparableTotal;
          return score;
        }
        default:
          return '';
      }
    },
    [viewedSettlements, settlementBalanceStatus]
  );

  const renderSettlementsTable = (settlements: Settlement[]) => {
    // Sort settlements
    const sortedSettlements = [...settlements];
    if (sortDescriptor.column) {
      sortedSettlements.sort((a, b) => {
        const aVal = getSortValue(a, sortDescriptor.column as string);
        const bVal = getSortValue(b, sortDescriptor.column as string);
        let cmp = 0;
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          cmp = aVal - bVal;
        } else {
          cmp = String(aVal).localeCompare(String(bVal));
        }
        return sortDescriptor.direction === 'descending' ? -cmp : cmp;
      });
    }

    // Calculate pagination values
    const totalPages = Math.ceil(sortedSettlements.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(
      startIndex + itemsPerPage,
      sortedSettlements.length
    );
    const paginatedSettlements = sortedSettlements.slice(startIndex, endIndex);

    return (
      <>
        <Table
          aria-label='Tabla de detalle de liquidaciones'
          classNames={UNIFIED_TABLE_CLASSNAMES}
          sortDescriptor={sortDescriptor}
          onSortChange={(descriptor) => {
            setSortDescriptor(descriptor);
            setCurrentPage(1);
          }}
        >
          <TableHeader columns={columns}>
            {(column) => (
              <TableColumn
                key={column.uid}
                align={column.uid === 'actions' ? 'center' : 'start'}
                allowsSorting={column.sortable}
                className={
                  column.uid === 'reference_number'
                    ? 'sticky left-0 z-20 bg-default-100'
                    : ''
                }
              >
                {column.name}
              </TableColumn>
            )}
          </TableHeader>
          <TableBody>
            {paginatedSettlements.map((item) => (
              <TableRow key={item.id}>
                {columns.map((column) => (
                  <TableCell
                    key={column.uid}
                    className={
                      column.uid === 'reference_number'
                        ? 'sticky left-0 z-10 bg-content1'
                        : ''
                    }
                  >
                    {renderCell(item, column.uid) as React.ReactNode}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Pagination controls */}
        {settlements.length > 0 && (
          <div className='flex justify-between items-center mt-4'>
            <div className='text-sm text-default-500'>
              {t('Mostrando {start} - {end} de {total} liquidaciones')
                .replace('{start}', String(startIndex + 1))
                .replace('{end}', String(endIndex))
                .replace('{total}', String(settlements.length))}
            </div>
            <div className='flex gap-2'>
              <Button
                size='sm'
                variant='light'
                isDisabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              >
                {t('Anterior')}
              </Button>
              <div className='flex items-center gap-2'>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  // Show first, last, current and 1 page before and after current
                  let pageToShow;

                  if (totalPages <= 5) {
                    pageToShow = i + 1;
                  } else if (currentPage <= 3) {
                    pageToShow = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageToShow = totalPages - 4 + i;
                  } else {
                    pageToShow = currentPage - 2 + i;
                  }

                  if (pageToShow <= totalPages) {
                    return (
                      <Button
                        key={pageToShow}
                        size='sm'
                        variant={currentPage === pageToShow ? 'solid' : 'light'}
                        color={currentPage === pageToShow ? 'primary' : 'default'}
                        onClick={() => setCurrentPage(pageToShow)}
                      >
                        {pageToShow}
                      </Button>
                    );
                  }
                  return null;
                })}

                {totalPages > 5 && currentPage < totalPages - 2 && (
                  <>
                    <span>...</span>
                    <Button
                      size='sm'
                      variant='light'
                      onClick={() => setCurrentPage(totalPages)}
                    >
                      {totalPages}
                    </Button>
                  </>
                )}
              </div>
              <Button
                size='sm'
                variant='light'
                isDisabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
              >
                {t('Siguiente')}
              </Button>
            </div>
          </div>
        )}
      </>
    );
  };

  // New function to sort settlements as required
  const getSortedSettlements = useMemo(() => {
    const { settlements: filteredSettlements } =
      getFilteredSettlements(settlements);

    // Check for settlements with missing data
    const missingExchangeRate = settlements.filter(
      (s) => !s.exchange_rate
    ).length;
    const missingFreight = settlements.filter(
      (s) => !s.total_freight_usd
    ).length;
    const totalIncomplete = settlements.filter(
      (s) => getSettlementIncoterm(s) !== 'fob'
    ).length;

    // Update the state with the counts
    setMissingDataSettlements({
      missingExchangeRate,
      missingFreight,
      totalIncomplete,
    });

    return [...filteredSettlements].sort((a, b) => {
      const weekA = getISOWeek(new Date(a.date_sold));
      const weekB = getISOWeek(new Date(b.date_sold));

      // Weeks < 27 should go first
      if ((weekA < 27 && weekB >= 27) || (weekA >= 27 && weekB < 27)) {
        return weekA < 27 ? -1 : 1;
      }

      // If both are in the same category (< 27 or >= 27), sort by week descending
      if (weekA !== weekB) {
        return weekB - weekA;
      }

      // If same week, sort by reference number descending
      const refA = a.reference_number || '';
      const refB = b.reference_number || '';
      return refB.localeCompare(refA);
    });
  }, [
    settlements,
    searchTerm,
    searchTermContainer,
    selectedClients,
    selectedShips,
    incotermFilter,
    selectedWeeks,
    selectedSeason,
    showOnlyPendingInfo,
    quadratureFilter,
    sinisterFilter,
    selectedCurrencies,
    showOnlyZeroValues,
    showOnlyKilosMismatch,
    kilosMismatchSettlements,
    showOnlyNegativeValues,
    negativeValueSettlements,
  ]);

  const hasAnyExtraRevenue = useMemo(
    () => settlements.some((s) => (s.total_other_revenue_local || 0) !== 0),
    [settlements]
  );

  const filteredSettlementIds = useMemo(
    () => new Set(getSortedSettlements.map((settlement) => settlement.id)),
    [getSortedSettlements]
  );

  return (
    <div className='flex w-full h-screen overflow-x-hidden'>
      <ModalSlideFilter
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
      >
        <div className='bg-background p-4 pt-8 h-full overflow-y-auto'>
          <div className='space-y-4'>
            <p className='text-sm text-default-500'>{t('Filtros')}</p>
            {hasSourceGroups && (
              <div className='space-y-2 border-b border-default-200 pb-4'>
                <p className='text-xs font-medium text-default-500'>
                  {t('Visualización')}
                </p>
                <div className='grid grid-cols-3 rounded-lg border border-default-200 bg-default-50 p-1 dark:bg-default-100'>
                  {(
                    [
                      ['reference', t('Referencia')],
                      ['document', t('Documento')],
                      ['group', t('Grupo')],
                    ] as Array<['reference' | SettlementSourceViewMode, string]>
                  ).map(([mode, label]) => (
                    <Button
                      key={mode}
                      size='sm'
                      className='min-w-0 px-2'
                      variant={settlementViewMode === mode ? 'solid' : 'light'}
                      color={
                        settlementViewMode === mode ? 'primary' : 'default'
                      }
                      onPress={() => setSettlementViewMode(mode)}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            <Input
              type='text'
              radius='sm'
              placeholder={t('Por Referencia')}
              className='w-full'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              startContent={<Icon icon='tabler:search' />}
            />
            <Input
              type='text'
              radius='sm'
              placeholder={t('Por Nº Contenedor')}
              className='w-full'
              value={searchTermContainer}
              onChange={(e) => setSearchTermContainer(e.target.value)}
              startContent={<Icon icon='tabler:search' />}
            />
            <SeasonFilter className='w-full' />

            <Select
              label={t('Semana')}
              selectionMode='multiple'
              placeholder={t('Seleccionar semanas')}
              size='sm'
              selectedKeys={new Set(selectedWeeks)}
              classNames={{
                trigger: 'min-h-unit-8',
                value: 'truncate',
              }}
              renderValue={(items) => {
                return (
                  <div className='flex gap-2'>
                    {selectedWeeks.includes('all') ? (
                      <span>{t('Todas las semanas')}</span>
                    ) : (
                      <span>
                        {items.length}{' '}
                        {items.length === 1 ? t('semana') : t('semanas')}{' '}
                        {t('seleccionadas')}
                      </span>
                    )}
                  </div>
                );
              }}
              onSelectionChange={(keys) => {
                const selectedKeys = Array.from(keys as Set<string>);
                if (selectedKeys.length === 0) {
                  setSelectedWeeks(['all']);
                } else if (
                  selectedKeys.includes('all') &&
                  !selectedWeeks.includes('all')
                ) {
                  setSelectedWeeks(['all']);
                } else if (
                  selectedWeeks.includes('all') &&
                  selectedKeys.length > 1
                ) {
                  setSelectedWeeks(selectedKeys.filter((key) => key !== 'all'));
                } else {
                  setSelectedWeeks(selectedKeys);
                }
              }}
              className='w-full'
            >
              <SelectItem key='all' value='all'>
                {t('Todas las semanas')}
              </SelectItem>

              {Array.from(
                new Set(
                  settlements.map((s) =>
                    getISOWeek(new Date(s.date_sold)).toString()
                  )
                )
              )
                .sort((a, b) => parseInt(a) - parseInt(b))
                .map((week) => (
                  <SelectItem key={week} value={week}>
                    {week}
                  </SelectItem>
                ))}
            </Select>
            <Select
              label={t('Clientes')}
              selectionMode='multiple'
              size='sm'
              placeholder={t('Seleccionar clientes')}
              selectedKeys={selectedClients}
              onSelectionChange={(keys) => {
                const selectedKeys = Array.from(keys as Set<string>);
                // Si no hay ninguna selección, establecer "all"
                if (selectedKeys.length === 0) {
                  setSelectedClients(['all']);
                }
                // Si se selecciona "all", deseleccionar otros y seleccionar solo "all"
                else if (
                  selectedKeys.includes('all') &&
                  !selectedClients.includes('all')
                ) {
                  setSelectedClients(['all']);
                }
                // Si se selecciona otro cuando "all" está seleccionado, quitar "all"
                else if (
                  selectedClients.includes('all') &&
                  selectedKeys.length > 1
                ) {
                  setSelectedClients(
                    selectedKeys.filter((key) => key !== 'all')
                  );
                }
                // En otros casos, actualizar normalmente
                else {
                  setSelectedClients(selectedKeys);
                }
              }}
              className='w-full'
            >
              <SelectItem key='all' value='all'>
                {t('Todos los clientes')}
              </SelectItem>
              {clients.map((client) => (
                <SelectItem key={client} value={client}>
                  {client}
                </SelectItem>
              ))}
            </Select>
            <Select
              label={t('Naves')}
              selectionMode='multiple'
              placeholder={t('Seleccionar naves')}
              size='sm'
              selectedKeys={selectedShips}
              onSelectionChange={(keys) => {
                const selectedKeys = Array.from(keys as Set<string>);
                // Si no hay ninguna selección, establecer "all"
                if (selectedKeys.length === 0) {
                  setSelectedShips(['all']);
                }
                // Si se selecciona "all", deseleccionar otros y seleccionar solo "all"
                else if (
                  selectedKeys.includes('all') &&
                  !selectedShips.includes('all')
                ) {
                  setSelectedShips(['all']);
                }
                // Si se selecciona otro cuando "all" está seleccionado, quitar "all"
                else if (
                  selectedShips.includes('all') &&
                  selectedKeys.length > 1
                ) {
                  setSelectedShips(selectedKeys.filter((key) => key !== 'all'));
                }
                // En otros casos, actualizar normalmente
                else {
                  setSelectedShips(selectedKeys);
                }
              }}
              className='w-full'
            >
              <SelectItem key='all' value='all'>
                {t('Todas las naves')}
              </SelectItem>
              {ships.map((ship) => (
                <SelectItem key={ship} value={ship}>
                  {ship}
                </SelectItem>
              ))}
            </Select>
            <Select
              label={t('Incoterm')}
              selectedKeys={[incotermFilter]}
              onChange={(e) => setIncotermFilter(e.target.value)}
              className='w-full'
              size='sm'
              defaultSelectedKeys={['all']}
            >
              <SelectItem key='all' value='all'>
                {t('Todos')}
              </SelectItem>
              {Array.from(
                new Set(settlements.map((s) => s.original_incoterm))
              ).map((incoterm) => (
                <SelectItem key={incoterm} value={incoterm}>
                  {incoterm}
                </SelectItem>
              ))}
              {/* <SelectItem key='pre_liquidacion' value='pre_liquidacion'>
                  Pre Liquidación
                </SelectItem>
                <SelectItem key='cif' value='cif'>
                  CIF
                </SelectItem>
                <SelectItem key='fob' value='fob'>
                  FOB
                </SelectItem> */}
            </Select>
            <Select
              label={t('Moneda')}
              selectionMode='multiple'
              placeholder={t('Seleccionar monedas')}
              size='sm'
              selectedKeys={new Set(selectedCurrencies)}
              onSelectionChange={(keys) => {
                const selectedKeys = Array.from(keys as Set<string>);
                if (selectedKeys.length === 0) {
                  setSelectedCurrencies(['all']);
                } else if (
                  selectedKeys.includes('all') &&
                  !selectedCurrencies.includes('all')
                ) {
                  setSelectedCurrencies(['all']);
                } else if (
                  selectedCurrencies.includes('all') &&
                  selectedKeys.length > 1
                ) {
                  setSelectedCurrencies(
                    selectedKeys.filter((key) => key !== 'all')
                  );
                } else {
                  setSelectedCurrencies(selectedKeys);
                }
              }}
              className='w-full'
            >
              <SelectItem key='all' value='all'>
                {t('Todas las monedas')}
              </SelectItem>
              {currencies.map((currency) => (
                <SelectItem key={currency} value={currency}>
                  {currency}
                </SelectItem>
              ))}
            </Select>
            <Select
              label={t('Estado Información')}
              selectedKeys={[showOnlyPendingInfo]}
              onChange={(e) => setShowOnlyPendingInfo(e.target.value)}
              className='w-full'
              size='sm'
              defaultSelectedKeys={['all']}
            >
              <SelectItem key='all' value='all'>
                {t('Todos')}
              </SelectItem>
              <SelectItem key='pending' value='pending'>
                {t('Falta Info')}
              </SelectItem>
              <SelectItem key='complete' value='complete'>
                OK
              </SelectItem>
            </Select>
            <Select
              label={t('Cuadratura con Despachos')}
              selectedKeys={[quadratureFilter]}
              onChange={(e) => setQuadratureFilter(e.target.value)}
              className='w-full'
              size='sm'
              defaultSelectedKeys={['all']}
            >
              <SelectItem key='all' value='all'>
                {t('Todas')}
              </SelectItem>
              <SelectItem key='mismatch' value='mismatch'>
                {t('No cuadradas')}
              </SelectItem>
              <SelectItem key='matched' value='matched'>
                {t('Cuadradas')}
              </SelectItem>
            </Select>
            <Select
              label={t('Estado Siniestros')}
              selectedKeys={[sinisterFilter]}
              onChange={(e) => setSinisterFilter(e.target.value)}
              className='w-full'
              size='sm'
              defaultSelectedKeys={['all']}
            >
              <SelectItem key='all' value='all'>
                {t('Todas')}
              </SelectItem>
              <SelectItem key='only_sinister' value='only_sinister'>
                {t('Solo Siniestros')}
              </SelectItem>
              <SelectItem key='no_sinister' value='no_sinister'>
                {t('Sin Siniestros')}
              </SelectItem>
            </Select>
          </div>
        </div>
      </ModalSlideFilter>
      {isEditOpen && (
        <EditSettlementDialog
          isOpen={isEditOpen}
          onClose={() => {
            setIsEditOpen(false);
            setSelectedSettlement(null);
          }}
          settlement={selectedSettlement}
          onEdit={() => {
            fetchSettlements();
          }}
          onDelete={() => {
            fetchSettlements();
            setIsEditOpen(false);
            setSelectedSettlement(null);
          }}
          onRevenueChange={() => {
            fetchSettlements();
          }}
        />
      )}
      {isSinisterModalOpen && (
        <SinisterSettlementDialog
          isOpen={isSinisterModalOpen}
          onClose={() => {
            setIsSinisterModalOpen(false);
            setSelectedSettlement(null);
          }}
          settlement={selectedSettlement}
          onUpdate={(id, isSinister, responsible, delayDays, caseNumber) => {
            setSettlements((prev) =>
              prev?.map((s) =>
                s.id === id
                  ? {
                      ...s,
                      is_sinister: isSinister,
                      sinister_responsible: responsible ?? null,
                      sinister_delay_days: delayDays ?? null,
                      sinister_case_number: caseNumber ?? null,
                    }
                  : s
              )
            );
          }}
        />
      )}
      {isBlockedModalOpen && (
        <BlockedSettlementDialog
          isOpen={isBlockedModalOpen}
          onClose={() => {
            setIsBlockedModalOpen(false);
            setSelectedSettlement(null);
          }}
          settlement={selectedSettlement}
          onUpdate={(id, isBlocked) => {
            setSettlements((prev) =>
              prev?.map((s) =>
                s.id === id ? { ...s, is_blocked: isBlocked } : s
              )
            );
          }}
        />
      )}
      <Modal
        isOpen={isQuadratureInfoOpen}
        onClose={() => setIsQuadratureInfoOpen(false)}
        size='md'
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className='flex flex-col gap-1'>
                {t('Cómo se realiza la cuadratura')}
              </ModalHeader>
              <ModalBody className='space-y-2 text-sm text-default-600'>
                <p>
                  {t(
                    'Se compara la cantidad de cajas despachadas vs liquidadas por referencia. Para despachos se agrupan las cajas por variedad etiquetada, calibre, color, formato, etiqueta y peso bulto y lo comparamos con lo liquidado.'
                  )}
                </p>
                <p>
                  {t(
                    'Si las cantidades coinciden para cada combinación, se considera cuadrada con despachos.'
                  )}
                </p>
                <p>
                  {t(
                    'Ejemplo: Si hay 5 cajas despachadas SANTINA - 2J - D - 2IN1 - GOLD - 5KG y 4 cajas liquidadas SANTINA - 2J - D - 2IN1 - GOLD - 5KG, se consideraría no cuadrada con despachos.'
                  )}
                </p>
              </ModalBody>
              <ModalFooter className='justify-end'>
                <Button variant='light' onPress={onClose}>
                  {t('Cerrar')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
      <Modal
        isOpen={isUnmappedReceiversOpen}
        onClose={() => {
          setIsUnmappedReceiversOpen(false);
          setReceiverSearchValues({});
          setReceiverAutocompleteOpenKeys({});
          setHiddenReceiverAutocompleteKey(null);
          closeCreateReceiverModal();
        }}
        size='3xl'
        scrollBehavior='inside'
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className='flex flex-col gap-1'>
                {t('Asociar clientes oficiales')}
                <p className='text-sm font-normal text-default-500'>
                  Asocia los siguientes nombres de clientes en tus
                  liquidaciones, con tu listado oficial de clientes. En caso de
                  no tener al cliente creado, lo puedes crear presionando en '+
                  Crear Nuevo Cliente'
                </p>
              </ModalHeader>
              <ModalBody>
                <div className='space-y-3'>
                  {unmappedReceiverGroups.map((group) => (
                    <div
                      key={group.key}
                      className='grid grid-cols-1 gap-3 rounded-md border border-default-200 p-3 md:grid-cols-[1fr_320px]'
                    >
                      <div>
                        <p className='font-medium'>{group.receiverName}</p>
                        <p className='text-sm text-default-500'>
                          {group.count} {t('liquidaciones')}
                        </p>
                      </div>
                      <div className='space-y-2'>
                        {hiddenReceiverAutocompleteKey === group.key ? (
                          <Input
                            label={t('Cliente oficial')}
                            value={t('Creando nuevo cliente...')}
                            isDisabled
                          />
                        ) : (
                          <Autocomplete
                            description='Esta lista proviene de tu Configuración, sección Clientes.'
                            label={t('Cliente oficial')}
                            selectedKey={receiverSelections[group.key] || null}
                            isOpen={
                              closingReceiverAutocompleteKeyRef.current ===
                                group.key ||
                              createReceiverGroup?.key === group.key
                                ? false
                                : receiverAutocompleteOpenKeys[group.key] ||
                                  false
                            }
                            onOpenChange={(open) => {
                              if (
                                closingReceiverAutocompleteKeyRef.current ===
                                  group.key ||
                                createReceiverGroup?.key === group.key
                              ) {
                                return;
                              }

                              setReceiverAutocompleteOpenKeys((prev) => ({
                                ...prev,
                                [group.key]: open,
                              }));
                            }}
                            onInputChange={(value) =>
                              setReceiverSearchValues((prev) => ({
                                ...prev,
                                [group.key]: value,
                              }))
                            }
                            onSelectionChange={(key) => {
                              const selectedKey = key?.toString() || '';
                              if (selectedKey === CREATE_RECEIVER_OPTION_KEY) {
                                openCreateReceiverModal(group);
                                return;
                              }

                              setReceiverSelections((prev) => ({
                                ...prev,
                                [group.key]: selectedKey,
                              }));
                              setReceiverAutocompleteOpenKeys((prev) => ({
                                ...prev,
                                [group.key]: false,
                              }));
                            }}
                          >
                            <AutocompleteItem
                              key={CREATE_RECEIVER_OPTION_KEY}
                              textValue={`Crear Nuevo Cliente ${
                                receiverSearchValues[group.key] ||
                                group.receiverName
                              }`}
                            >
                              + Crear Nuevo Cliente
                            </AutocompleteItem>
                            {exporterReceivers.map((receiver) => (
                              <AutocompleteItem
                                key={receiver.id.toString()}
                                textValue={receiver.name}
                              >
                                {receiver.name}
                              </AutocompleteItem>
                            ))}
                          </Autocomplete>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  variant='light'
                  onPress={() => {
                    setReceiverSearchValues({});
                    setReceiverAutocompleteOpenKeys({});
                    setHiddenReceiverAutocompleteKey(null);
                    closeCreateReceiverModal();
                    onClose();
                  }}
                >
                  {t('Cancelar')}
                </Button>
                <Button
                  color='primary'
                  isLoading={savingReceiverMappings}
                  onPress={handleSaveUnmappedReceiverMappings}
                >
                  {t('Confirmar')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
      <Modal
        isOpen={Boolean(createReceiverGroup)}
        onClose={closeCreateReceiverModal}
        size='md'
      >
        <ModalContent>
          <ModalHeader className='flex flex-col gap-1'>
            {t('Crear Nuevo Cliente')}
            {createReceiverGroup && (
              <p className='text-sm font-normal text-default-500'>
                {t('Se agregará a Configuración, sección Clientes.')}
              </p>
            )}
          </ModalHeader>
          <ModalBody>
            <Input
              label={t('Nombre del cliente')}
              value={createReceiverName}
              onChange={(event) => setCreateReceiverName(event.target.value)}
              autoFocus
            />
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onPress={closeCreateReceiverModal}>
              {t('Cancelar')}
            </Button>
            <Button
              color='primary'
              isLoading={isCreatingReceiver}
              onPress={handleCreateReceiver}
            >
              {t('Crear cliente')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={isSourcePdfOpen}
        onClose={() => {
          setIsSourcePdfOpen(false);
          setSourcePdfUrl('');
        }}
        size='5xl'
        scrollBehavior='inside'
        classNames={{ base: 'h-[92dvh] max-h-[92dvh]' }}
      >
        <ModalContent>
          <ModalHeader className='flex items-center gap-2'>
            <Icon icon='tabler:file-type-pdf' className='text-danger' />
            <span className='truncate'>{sourcePdfTitle}</span>
            <Chip size='sm' variant='flat'>
              {t('Página')} {sourcePdfPage}
            </Chip>
          </ModalHeader>
          <ModalBody className='min-h-0 p-0'>
            {isSourcePdfLoading || !sourcePdfUrl ? (
              <div className='flex min-h-[420px] items-center justify-center'>
                <Spinner label={t('Abriendo PDF original')} />
              </div>
            ) : (
              <iframe
                title={sourcePdfTitle || t('PDF original')}
                src={`${sourcePdfUrl}#page=${sourcePdfPage}`}
                className='min-h-0 flex-1 bg-content1'
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
      <SettlementHistoryDrawer
        isOpen={isHistoryOpen}
        onClose={() => {
          setIsHistoryOpen(false);
          setSelectedHistorySettlement(null);
        }}
        settlement={selectedHistorySettlement}
        onChange={reloadHistoryCounts}
      />
      <PageLayout>
        <Header
          title={t('Detalle Liquidaciones')}
          description={t('Revisa por separado las liquidaciones recibidas')}
          actions={
            <div className='flex items-center gap-2'>
              {canUseMarketComparisonRecalculate && (
                <Button
                  variant='flat'
                  startContent={<Icon icon='tabler:robot' />}
                  isLoading={isRecalculatingMarketComparisons}
                  isDisabled={isRecalculatingMarketComparisons}
                  onPress={recalculateAllMarketComparisons}
                >
                  {isRecalculatingMarketComparisons
                    ? t('Recalculando...')
                    : t('Recalcular mercado')}
                </Button>
              )}

              {/* Export */}
              <Dropdown>
                <DropdownTrigger>
                  <Button
                    variant='flat'
                    startContent={<Icon icon='tabler:download' />}
                    endContent={<Icon icon='tabler:chevron-down' className='text-xs' />}
                    isDisabled={downloadingExcel || downloadingExcelWithRevenue}
                  >
                    {downloadingExcel || downloadingExcelWithRevenue
                      ? t('Descargando...')
                      : t('Exportar')}
                  </Button>
                </DropdownTrigger>
                <DropdownMenu
                  aria-label='Export options'
                  onAction={async (key) => {
                    if (key === 'standard') {
                      setDownloadingExcel(true);
                      try {
                        const wb = XLSX.utils.book_new();
                        const [data, shipmentBoxes] = await Promise.all([
                          getSettlementsByExporterId(
                            exporter_id,
                            'approved' as any,
                            selectedSeason,
                            selectedSpecies?.id,
                            'all',
                            ['*'],
                            currentDivisionId()
                          ),
                          getShipmentBoxesForExport(
                            exporter_id,
                            selectedSeason,
                            selectedSpecies?.id as number
                          ),
                        ]);
                        if (!data) {
                          toast.error(t('No se pudieron obtener los datos'));
                          return;
                        }
                        const detailsData = prepareSettlementDetailsData(
                          data,
                          t,
                          shipmentBoxes || []
                        );
                        const wsDetails = XLSX.utils.json_to_sheet(detailsData);
                        const range = XLSX.utils.decode_range(
                          wsDetails['!ref'] || 'A1'
                        );
                        let moneyColumns = [
                          20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32,
                          33, 34, 35,
                        ];
                        try {
                          const headers = Object.keys(detailsData[0] || {});
                          const idx = headers.indexOf(
                            t('Diferencia vs Mercado ETA $')
                          );
                          if (idx >= 0 && !moneyColumns.includes(idx))
                            moneyColumns.push(idx);
                        } catch (e) {
                          /* no-op */
                        }
                        wsDetails['!cols'] = Array(range.e.c + 1).fill({
                          wch: 12,
                        });
                        moneyColumns.forEach((col) => {
                          if (wsDetails['!cols'])
                            wsDetails['!cols'][col] = { wch: 15 };
                        });
                        for (let row = range.s.r; row <= range.e.r; row++) {
                          for (const col of moneyColumns) {
                            const cell = XLSX.utils.encode_cell({
                              r: row,
                              c: col,
                            });
                            if (!wsDetails[cell]) continue;
                            wsDetails[cell].z =
                              '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "-"??_);_(@_)';
                          }
                        }
                        XLSX.utils.book_append_sheet(
                          wb,
                          wsDetails,
                          t('Detalle')
                        );
                        const settlementsData = prepareSettlementsData(data, t);
                        const wsSettlements =
                          XLSX.utils.json_to_sheet(settlementsData);
                        const range2 = XLSX.utils.decode_range(
                          wsSettlements['!ref'] || 'A1'
                        );
                        wsSettlements['!cols'] = Array(range2.e.c + 1).fill({
                          wch: 12,
                        });
                        wsSettlements['!cols'][range2.e.c] = { wch: 18 };
                        for (let row = range2.s.r; row <= range2.e.r; row++) {
                          const cell = XLSX.utils.encode_cell({
                            r: row,
                            c: range2.e.c,
                          });
                          if (!wsSettlements[cell]) continue;
                          wsSettlements[cell].z =
                            '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "-"??_);_(@_)';
                        }
                        XLSX.utils.book_append_sheet(
                          wb,
                          wsSettlements,
                          t('Liquidaciones')
                        );
                        XLSX.writeFile(wb, t('Liquidaciones') + '.xlsx');
                      } catch (error) {
                        console.error(
                          'Error al exportar liquidaciones:',
                          error
                        );
                        toast.error(t('Error al exportar liquidaciones'));
                      } finally {
                        setDownloadingExcel(false);
                      }
                    } else if (key === 'exporter-standard') {
                      if (!exporter_id) return;
                      setDownloadingExcel(true);
                      try {
                        const [data, shipmentBoxes] = await Promise.all([
                          getSettlementsByExporterId(
                            exporter_id,
                            'approved' as any,
                            selectedSeason,
                            selectedSpecies?.id,
                            'all',
                            ['*'],
                            currentDivisionId()
                          ),
                          getShipmentBoxesForExport(
                            exporter_id,
                            selectedSeason,
                            selectedSpecies?.id as number
                          ),
                        ]);
                        if (!data) {
                          toast.error(t('No se pudieron obtener los datos'));
                          return;
                        }
                        const [categories, costs] = await Promise.all([
                          getExporterCostCategories(exporter_id, true),
                          getSettlementExporterCosts(data.map((s) => s.id)),
                        ]);
                        if (costs.length === 0) {
                          toast.info(
                            t(
                              'Esta temporada no tiene liquidaciones con estándar exportadora.'
                            )
                          );
                          return;
                        }
                        const detailsData = toExporterCostStandardRows(
                          prepareSettlementDetailsData(
                            data,
                            t,
                            shipmentBoxes || []
                          ),
                          data,
                          costs,
                          categories,
                          t
                        );
                        const wb = XLSX.utils.book_new();
                        XLSX.utils.book_append_sheet(
                          wb,
                          XLSX.utils.json_to_sheet(detailsData),
                          t('Detalle')
                        );
                        XLSX.writeFile(
                          wb,
                          t('Liquidaciones_Estandar_Exportadora') + '.xlsx'
                        );
                      } catch (error) {
                        console.error(
                          'Error al exportar estándar exportadora:',
                          error
                        );
                        toast.error(t('Error al exportar liquidaciones'));
                      } finally {
                        setDownloadingExcel(false);
                      }
                    } else if (key === 'with-revenue') {
                      setDownloadingExcelWithRevenue(true);
                      try {
                        const wb = XLSX.utils.book_new();
                        const [data, shipmentBoxes2] = await Promise.all([
                          getSettlementsByExporterId(
                            exporter_id,
                            'approved' as any,
                            selectedSeason,
                            selectedSpecies?.id,
                            'all',
                            ['*'],
                            currentDivisionId()
                          ),
                          getShipmentBoxesForExport(
                            exporter_id,
                            selectedSeason,
                            selectedSpecies?.id as number
                          ),
                        ]);
                        if (!data) {
                          toast.error(t('No se pudieron obtener los datos'));
                          return;
                        }
                        const detailsData =
                          prepareSettlementDetailsDataWithRevenue(
                            data,
                            t,
                            shipmentBoxes2 || []
                          );
                        const wsDetails = XLSX.utils.json_to_sheet(detailsData);
                        const range = XLSX.utils.decode_range(
                          wsDetails['!ref'] || 'A1'
                        );
                        wsDetails['!cols'] = Array(range.e.c + 1).fill({
                          wch: 12,
                        });
                        const headers = Object.keys(detailsData[0] || {});
                        const moneyColNames = [
                          t('Otros Ingresos Moneda Local'),
                          t('Venta Ajustada Moneda Local'),
                          t('CIF Ajustado Moneda Local'),
                          t('FOB Ajustado Moneda Local'),
                          t('Otros Ingresos USD'),
                          t('CIF Ajustado USD'),
                          t('FOB Ajustado USD'),
                        ];
                        const moneyColumns = moneyColNames
                          .map((n) => headers.indexOf(n))
                          .filter((i) => i >= 0);
                        [
                          20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32,
                          33, 34, 35,
                        ].forEach((col) => {
                          if (!moneyColumns.includes(col))
                            moneyColumns.push(col);
                        });
                        moneyColumns.forEach((col) => {
                          if (wsDetails['!cols'])
                            wsDetails['!cols'][col] = { wch: 15 };
                        });
                        for (let row = range.s.r; row <= range.e.r; row++) {
                          for (const col of moneyColumns) {
                            const cell = XLSX.utils.encode_cell({
                              r: row,
                              c: col,
                            });
                            if (!wsDetails[cell]) continue;
                            wsDetails[cell].z =
                              '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "-"??_);_(@_)';
                          }
                        }
                        XLSX.utils.book_append_sheet(
                          wb,
                          wsDetails,
                          t('Detalle')
                        );
                        const settlementsData =
                          prepareSettlementsDataWithRevenue(data, t);
                        const wsSettlements =
                          XLSX.utils.json_to_sheet(settlementsData);
                        const range2 = XLSX.utils.decode_range(
                          wsSettlements['!ref'] || 'A1'
                        );
                        wsSettlements['!cols'] = Array(range2.e.c + 1).fill({
                          wch: 12,
                        });
                        for (
                          let col = range2.e.c - 2;
                          col <= range2.e.c;
                          col++
                        ) {
                          if (wsSettlements['!cols'])
                            wsSettlements['!cols'][col] = { wch: 18 };
                          for (let row = range2.s.r; row <= range2.e.r; row++) {
                            const cell = XLSX.utils.encode_cell({
                              r: row,
                              c: col,
                            });
                            if (!wsSettlements[cell]) continue;
                            wsSettlements[cell].z =
                              '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "-"??_);_(@_)';
                          }
                        }
                        XLSX.utils.book_append_sheet(
                          wb,
                          wsSettlements,
                          t('Liquidaciones')
                        );
                        XLSX.writeFile(
                          wb,
                          t('Liquidaciones_con_Ingresos_Adicionales') + '.xlsx'
                        );
                      } catch (error) {
                        console.error(
                          'Error al exportar liquidaciones con ingresos:',
                          error
                        );
                        toast.error(t('Error al exportar liquidaciones'));
                      } finally {
                        setDownloadingExcelWithRevenue(false);
                      }
                    }
                  }}
                >
                  <DropdownItem
                    key='standard'
                    startContent={<Icon icon='tabler:file-spreadsheet' />}
                    description={
                      exporterCostViewAvailable
                        ? t(
                            'Costos Estándar FruitMarkets: comisión, impuestos, flete, repacking y otros costos'
                          )
                        : undefined
                    }
                  >
                    {t('Exportar Liquidaciones')}
                  </DropdownItem>
                  {exporterCostViewAvailable ? (
                    <DropdownItem
                      key='exporter-standard'
                      startContent={<Icon icon='tabler:file-spreadsheet' />}
                      description={t(
                        'Costos según tus categorías y su prorrateo, en moneda local y USD (desde 2026-2027)'
                      )}
                    >
                      {t('Exportar con Estándar Exportadora')}
                    </DropdownItem>
                  ) : null}
                  {hasAnyExtraRevenue ? (
                    <DropdownItem
                      key='with-revenue'
                      startContent={<Icon icon='tabler:file-spreadsheet' />}
                      className='text-green-600 dark:text-green-400'
                    >
                      {t('Exportar con Ingresos Adicionales')}
                    </DropdownItem>
                  ) : null}
                </DropdownMenu>
              </Dropdown>

              {/* Upload */}
              <Dropdown>
                <DropdownTrigger>
                  <Button
                    color='primary'
                    startContent={<Icon icon='tabler:upload' />}
                    endContent={<Icon icon='tabler:chevron-down' className='text-xs' />}
                  >
                    {t('Subir')}
                  </Button>
                </DropdownTrigger>
                <DropdownMenu
                  aria-label='Upload options'
                  onAction={(key) => {
                    if (key === 'excel') setIsExcelUploadOpen(true);
                    else if (key === 'manual') setIsManualUploadOpen(true);
                    else if (key === 'shipments')
                      setIsShipmentsUploadOpen(true);
                    else if (key === 'excel-massive')
                      setIsExcelMassiveUploadOpen(true);
                    else if (key === 'multi-shipment')
                      setIsMultiShipmentUploadOpen(true);
                    else if (key === 'otros-ingresos')
                      setIsMassiveRevenueUploadOpen(true);
                    else if (key === 'costos')
                      setIsMassiveCostsUploadOpen(true);
                  }}
                >
                  <DropdownSection title={t('Single Shipment')} showDivider>
                    <DropdownItem
                      key='excel'
                      startContent={<Icon icon='tabler:robot' />}
                      description={t('Descripción FruitMarkets')}
                    >
                      {t('Vía Robot')}
                    </DropdownItem>
                    <DropdownItem
                      key='shipments'
                      startContent={<Icon icon='tabler:truck' />}
                      description={t('Descripción Planilla de Despachos')}
                    >
                      {t('Vía Planilla de Despachos')}
                    </DropdownItem>
                    <DropdownItem
                      key='manual'
                      startContent={<Icon icon='tabler:hand-stop' />}
                      description={t('Descripción Manual')}
                    >
                      {t('Vía Manual')}
                    </DropdownItem>
                  </DropdownSection>
                  <DropdownSection title={t('Multi-Shipment')} showDivider>
                    <DropdownItem
                      key='multi-shipment'
                      startContent={<Icon icon='tabler:upload' />}
                      description={t('Descripción archivo multi-embarque')}
                    >
                      {t('Vía Archivo Multi-Embarque')}
                    </DropdownItem>
                    <DropdownItem
                      key='excel-massive'
                      startContent={<Icon icon='tabler:file-spreadsheet' />}
                      description={t('Descripción Excel Masivo')}
                    >
                      {t('Vía Excel Masivo')}
                    </DropdownItem>
                  </DropdownSection>
                  <DropdownSection title={t('Otros Ingresos')} showDivider>
                    <DropdownItem
                      key='otros-ingresos'
                      startContent={<Icon icon='tabler:file-spreadsheet' />}
                      description={t(
                        'Carga masiva de otros ingresos vía Excel'
                      )}
                    >
                      {t('Vía Excel')}
                    </DropdownItem>
                  </DropdownSection>
                  <DropdownSection title={t('Costos')}>
                    <DropdownItem
                      key='costos'
                      startContent={<Icon icon='tabler:file-spreadsheet' />}
                      description={t('Carga masiva de costos vía Excel')}
                    >
                      {t('Vía Excel')}
                    </DropdownItem>
                  </DropdownSection>
                </DropdownMenu>
              </Dropdown>
            </div>
          }
        />

        {unmappedReceiverGroups.length > 0 && (
          <Alert variant='warning' showIcon className='mb-4'>
            <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
              <div>
                <p className='font-semibold'>
                  {unmappedReceiverGroups.reduce(
                    (total, group) => total + group.count,
                    0
                  )}{' '}
                  {t('liquidaciones sin cliente oficial')}
                </p>
                <p className='text-sm'>
                  {unmappedReceiverGroups.length}{' '}
                  {t('nombres de cliente necesitan asociación')}
                </p>
              </div>
              <Button
                variant='flat'
                startContent={<Icon icon='tabler:alert-triangle' />}
                onPress={() => setIsUnmappedReceiversOpen(true)}
              >
                {t('Asociar clientes')}
              </Button>
            </div>
          </Alert>
        )}

        {/* Toolbar */}
        <div className='flex flex-wrap items-center gap-2 mb-4'>
          <FiltersToggle
            isOpen={isFilterOpen}
            onToggle={() => setIsFilterOpen(!isFilterOpen)}
            label={isFilterOpen ? t('Ocultar filtros') : t('Mostrar filtros')}
          />

          {settlementViewMode !== 'reference' && (
            <Input
              size='sm'
              className='w-full sm:max-w-xs'
              value={sourceSearchTerm}
              onValueChange={setSourceSearchTerm}
              placeholder={t('Buscar documento, grupo o referencia')}
              startContent={<Icon icon='tabler:search' />}
              isClearable
              onClear={() => setSourceSearchTerm('')}
            />
          )}

          {hasAnyExtraRevenue && (
            <Switch
              size='sm'
              isSelected={showWithRevenues}
              onValueChange={setShowWithRevenues}
            >
              <span className='text-sm'>
                {t('Ver con ingresos adicionales')}
              </span>
            </Switch>
          )}
        </div>

        {/* Active filter chips */}
        {(searchTerm ||
          searchTermContainer ||
          !selectedClients.includes('all') ||
          !selectedShips.includes('all') ||
          incotermFilter !== 'all' ||
          !selectedWeeks.includes('all') ||
          !selectedCurrencies.includes('all') ||
          showOnlyPendingInfo !== 'all' ||
          quadratureFilter !== 'all' ||
          sinisterFilter !== 'all' ||
          showOnlyZeroValues ||
          showOnlyKilosMismatch ||
          showOnlyNegativeValues) && (
          <div className='flex flex-wrap items-center gap-2 mb-4'>
            {searchTerm && (
              <Chip
                size='sm'
                variant='bordered'
                onClose={() => setSearchTerm('')}
              >
                {t('Referencia')}: {searchTerm}
              </Chip>
            )}
            {searchTermContainer && (
              <Chip
                size='sm'
                variant='bordered'
                onClose={() => setSearchTermContainer('')}
              >
                {t('Contenedor')}: {searchTermContainer}
              </Chip>
            )}
            {!selectedClients.includes('all') && (
              <Chip
                size='sm'
                variant='bordered'
                onClose={() => setSelectedClients(['all'])}
              >
                {t('Clientes')}:{' '}
                {selectedClients.length === 1
                  ? selectedClients[0]
                  : `${selectedClients.length} ${t('seleccionados')}`}
              </Chip>
            )}
            {!selectedShips.includes('all') && (
              <Chip
                size='sm'
                variant='bordered'
                onClose={() => setSelectedShips(['all'])}
              >
                {t('Naves')}:{' '}
                {selectedShips.length === 1
                  ? selectedShips[0]
                  : `${selectedShips.length} ${t('seleccionadas')}`}
              </Chip>
            )}
            {!selectedWeeks.includes('all') && (
              <Chip
                size='sm'
                variant='bordered'
                onClose={() => setSelectedWeeks(['all'])}
              >
                {t('Semanas')}:{' '}
                {selectedWeeks.length === 1
                  ? selectedWeeks[0]
                  : `${selectedWeeks.length} ${t('seleccionadas')}`}
              </Chip>
            )}
            {incotermFilter !== 'all' && (
              <Chip
                size='sm'
                variant='bordered'
                onClose={() => setIncotermFilter('all')}
              >
                {t('Incoterm')}: {incotermFilter}
              </Chip>
            )}
            {!selectedCurrencies.includes('all') && (
              <Chip
                size='sm'
                variant='bordered'
                onClose={() => setSelectedCurrencies(['all'])}
              >
                {t('Moneda')}:{' '}
                {selectedCurrencies.length === 1
                  ? selectedCurrencies[0]
                  : `${selectedCurrencies.length} ${t('seleccionadas')}`}
              </Chip>
            )}
            {showOnlyPendingInfo !== 'all' && (
              <Chip
                size='sm'
                variant='bordered'
                onClose={() => setShowOnlyPendingInfo('all')}
              >
                {t('Estado')}:{' '}
                {showOnlyPendingInfo === 'pending'
                  ? t('Incompletas')
                  : t('Completas')}
              </Chip>
            )}
            {quadratureFilter !== 'all' && (
              <Chip
                size='sm'
                variant='bordered'
                onClose={() => setQuadratureFilter('all')}
              >
                {t('Cuadratura')}:{' '}
                {quadratureFilter === 'mismatch'
                  ? t('No cuadradas')
                  : t('Cuadradas')}
              </Chip>
            )}
            {sinisterFilter !== 'all' && (
              <Chip
                size='sm'
                variant='bordered'
                onClose={() => setSinisterFilter('all')}
              >
                {t('Siniestros')}:{' '}
                {sinisterFilter === 'only_sinister'
                  ? t('Solo Siniestros')
                  : t('Sin Siniestros')}
              </Chip>
            )}
            {showOnlyZeroValues && (
              <Chip
                size='sm'
                variant='bordered'
                onClose={() => setShowOnlyZeroValues(false)}
              >
                {t('Valores en cero')}
              </Chip>
            )}
            {showOnlyKilosMismatch && (
              <Chip
                size='sm'
                variant='bordered'
                onClose={() => setShowOnlyKilosMismatch(false)}
              >
                {t('Kilos inconsistentes')}
              </Chip>
            )}
            {showOnlyNegativeValues && (
              <Chip
                size='sm'
                variant='bordered'
                onClose={() => setShowOnlyNegativeValues(false)}
              >
                {t('Valores negativos')}
              </Chip>
            )}
            <Button
              size='sm'
              variant='flat'
              onClick={() => {
                setSearchTerm('');
                setSearchTermContainer('');
                setSelectedClients(['all']);
                setSelectedShips(['all']);
                setIncotermFilter('all');
                setSelectedWeeks(['all']);
                setSelectedCurrencies(['all']);
                setShowOnlyPendingInfo('all');
                setQuadratureFilter('all');
                setSinisterFilter('all');
                setShowOnlyZeroValues(false);
                setShowOnlyKilosMismatch(false);
                setShowOnlyNegativeValues(false);
              }}
              startContent={<Icon icon='tabler:x' />}
              className='text-sm'
            >
              {t('Limpiar todos')}
            </Button>
          </div>
        )}

        {/* Checks */}
        <div className='rounded-lg border border-default-200 mb-4 divide-y divide-default-200'>
          {/* Quadrature check */}
          {(() => {
            const hasIssue =
              isQuadratureReady && quadratureSummary.mismatch > 0;
            const isExpanded = expandedCheck === 'quadrature';
            return (
              <div>
                <div
                  role='button'
                  tabIndex={0}
                  className='w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-default-50 transition-colors'
                  onClick={() =>
                    hasIssue &&
                    setExpandedCheck(isExpanded ? null : 'quadrature')
                  }
                  onKeyDown={(event) => {
                    if (
                      hasIssue &&
                      (event.key === 'Enter' || event.key === ' ')
                    ) {
                      event.preventDefault();
                      setExpandedCheck(isExpanded ? null : 'quadrature');
                    }
                  }}
                >
                  <div className='flex items-center gap-3 min-w-0'>
                    {shipmentsError ? (
                      <Icon icon='tabler:alert-triangle' className='text-danger flex-shrink-0' />
                    ) : !isQuadratureReady ? (
                      <Spinner size='sm' className='flex-shrink-0' />
                    ) : hasIssue ? (
                      <Icon icon='tabler:alert-triangle' className='text-warning flex-shrink-0' />
                    ) : (
                      <Icon icon='tabler:circle-check' className='text-success flex-shrink-0' />
                    )}
                    <span className='text-sm font-semibold truncate'>
                      {t('Cuadratura con Despachos')}
                    </span>
                    <Tooltip content={t('Ver cómo se calcula la cuadratura')}>
                      <Button
                        variant='light'
                        size='sm'
                        isIconOnly
                        onPress={() => {
                          setIsQuadratureInfoOpen(true);
                        }}
                        className='text-default-400 hover:text-primary min-w-6 w-6 h-6'
                      >
                        <Icon icon='tabler:help-circle' className='text-xs' />
                      </Button>
                    </Tooltip>
                  </div>
                  <div className='flex items-center gap-3 flex-shrink-0'>
                    <span className='text-sm text-default-500'>
                      {shipmentsError
                        ? t('No se pudieron cargar los despachos')
                        : !isQuadratureReady
                          ? t('Calculando...')
                          : hasIssue
                            ? `${quadratureSummary.mismatch} ${t('no cuadradas')}`
                            : t('Todo cuadrado')}
                    </span>
                    {hasIssue && (
                      <Icon icon='tabler:chevron-down'
                        className={`text-xs text-default-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      />
                    )}
                  </div>
                </div>
                {isExpanded && (
                  <div className='px-4 pb-4 pt-2 space-y-3'>
                    <div className='flex flex-wrap items-center justify-between gap-4'>
                      <div className='flex flex-wrap items-center gap-4 text-sm text-default-500'>
                        <div className='flex items-center gap-2'>
                          <span className='inline-flex h-2 w-2 rounded-full bg-emerald-500' />
                          <span>
                            {t('Cuadradas')}:{' '}
                            <span className='font-semibold text-foreground'>
                              {quadratureSummary.matched}
                            </span>
                          </span>
                        </div>
                        <div className='flex items-center gap-2'>
                          <span className='inline-flex h-2 w-2 rounded-full bg-rose-500' />
                          <span>
                            {t('No cuadradas')}:{' '}
                            <span className='font-semibold text-foreground'>
                              {quadratureSummary.mismatch}
                            </span>
                          </span>
                        </div>
                      </div>
                      <div className='flex items-center gap-2'>
                        <Button
                          variant='flat'
                          size='sm'
                          onPress={() =>
                            setQuadratureFilter(
                              quadratureFilter === 'mismatch'
                                ? 'all'
                                : 'mismatch'
                            )
                          }
                        >
                          {quadratureFilter === 'mismatch'
                            ? t('Ver todas')
                            : `${t('Ver no cuadradas')} (${quadratureSummary.mismatch})`}
                        </Button>
                        <Button
                          variant='flat'
                          size='sm'
                          startContent={<Icon icon='tabler:file-spreadsheet' className='text-sm' />}
                          onPress={exportQuadratureDifferences}
                        >
                          {t('Exportar Diferencias')} (
                          {quadratureSummary.mismatch})
                        </Button>
                      </div>
                    </div>
                    <div className='h-1.5 w-full overflow-hidden rounded-full bg-default-200'>
                      <div className='flex h-full'>
                        <div
                          className='bg-emerald-500'
                          style={{
                            width:
                              quadratureSummary.total > 0
                                ? `${
                                    (quadratureSummary.matched /
                                      quadratureSummary.total) *
                                    100
                                  }%`
                                : '0%',
                          }}
                        />
                        <div
                          className='bg-rose-500'
                          style={{
                            width:
                              quadratureSummary.total > 0
                                ? `${
                                    (quadratureSummary.mismatch /
                                      quadratureSummary.total) *
                                    100
                                  }%`
                                : '0%',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Incomplete data check — only shown when issues exist */}
          {(() => {
            const hasIssue =
              missingDataSettlements.missingExchangeRate > 0 ||
              missingDataSettlements.missingFreight > 0;
            if (!hasIssue) return null;
            const isExpanded = expandedCheck === 'incomplete';
            return (
              <div>
                <button
                  type='button'
                  className='w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-default-50 transition-colors'
                  onClick={() =>
                    hasIssue &&
                    setExpandedCheck(isExpanded ? null : 'incomplete')
                  }
                >
                  <div className='flex items-center gap-3 min-w-0'>
                    {hasIssue ? (
                      <Icon icon='tabler:alert-triangle' className='text-warning flex-shrink-0' />
                    ) : (
                      <Icon icon='tabler:circle-check' className='text-success flex-shrink-0' />
                    )}
                    <span className='text-sm font-semibold truncate'>
                      {t('Liquidaciones Incompletas')}
                    </span>
                  </div>
                  <div className='flex items-center gap-3 flex-shrink-0'>
                    <span className='text-sm text-default-500'>
                      {hasIssue
                        ? `${missingDataSettlements.totalIncomplete} ${t('incompletas')}`
                        : t('Todo completo')}
                    </span>
                    {hasIssue && (
                      <Icon icon='tabler:chevron-down'
                        className={`text-xs text-default-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      />
                    )}
                  </div>
                </button>
                {isExpanded && (
                  <div className='px-4 pb-4 pt-2'>
                    <div className='flex flex-wrap items-center justify-between gap-4'>
                      <p className='text-sm text-default-500'>
                        {missingDataSettlements.missingExchangeRate > 0 && (
                          <span>
                            {missingDataSettlements.missingExchangeRate}{' '}
                            {t('sin tipo de cambio')}
                          </span>
                        )}
                        {missingDataSettlements.missingExchangeRate > 0 &&
                          missingDataSettlements.missingFreight > 0 &&
                          ', '}
                        {missingDataSettlements.missingFreight > 0 && (
                          <span>
                            {missingDataSettlements.missingFreight}{' '}
                            {t('sin valor de flete')}
                          </span>
                        )}
                      </p>
                      <Button
                        size='sm'
                        variant='flat'
                        onClick={() =>
                          setShowOnlyPendingInfo(
                            showOnlyPendingInfo === 'pending'
                              ? 'all'
                              : 'pending'
                          )
                        }
                      >
                        {showOnlyPendingInfo === 'pending'
                          ? t('Ver todas')
                          : `${t('Ver incompletas')} (${missingDataSettlements.totalIncomplete})`}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Zero values check — only shown when issues exist */}
          {(() => {
            const hasIssue = zeroValueSettlements.length > 0;
            if (!hasIssue) return null;
            const isExpanded = expandedCheck === 'zerovalues';
            return (
              <div>
                <button
                  type='button'
                  className='w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-default-50 transition-colors'
                  onClick={() =>
                    hasIssue &&
                    setExpandedCheck(isExpanded ? null : 'zerovalues')
                  }
                >
                  <div className='flex items-center gap-3 min-w-0'>
                    {hasIssue ? (
                      <Icon icon='tabler:alert-triangle' className='text-danger flex-shrink-0' />
                    ) : (
                      <Icon icon='tabler:circle-check' className='text-success flex-shrink-0' />
                    )}
                    <span className='text-sm font-semibold truncate'>
                      {t('Valores en Cero')}
                    </span>
                  </div>
                  <div className='flex items-center gap-3 flex-shrink-0'>
                    <span className='text-sm text-default-500'>
                      {hasIssue
                        ? `${zeroValueSettlements.length} ${t('liquidaciones afectadas')}`
                        : t('Sin problemas')}
                    </span>
                    {hasIssue && (
                      <Icon icon='tabler:chevron-down'
                        className={`text-xs text-default-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      />
                    )}
                  </div>
                </button>
                {isExpanded && (
                  <div className='px-4 pb-4 pt-2'>
                    <div className='flex flex-wrap items-start justify-between gap-4'>
                      <div className='text-sm text-default-500'>
                        <p>
                          {zeroValueSettlements.length}{' '}
                          {t(
                            'liquidaciones tienen líneas de detalle con cantidad de bultos/peso bulto con valor 0. Son las siguientes referencias:'
                          )}
                        </p>
                        <p className='text-default-400 mt-1'>
                          {zeroValueSettlements.join(', ')}
                        </p>
                      </div>
                      <Button
                        size='sm'
                        variant='flat'
                        className='flex-shrink-0'
                        onPress={() =>
                          setShowOnlyZeroValues(!showOnlyZeroValues)
                        }
                      >
                        {showOnlyZeroValues
                          ? t('Ver todas')
                          : `${t('Ver valores en cero')} (${zeroValueSettlements.length})`}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Stored kilos consistency check — only shown when issues exist */}
          {(() => {
            const hasIssue = kilosMismatchSettlements.length > 0;
            if (!hasIssue) return null;
            const isExpanded = expandedCheck === 'kilosmismatch';
            return (
              <div>
                <button
                  type='button'
                  className='w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-default-50 transition-colors'
                  onClick={() =>
                    hasIssue &&
                    setExpandedCheck(isExpanded ? null : 'kilosmismatch')
                  }
                >
                  <div className='flex items-center gap-3 min-w-0'>
                    {hasIssue ? (
                      <Icon icon='tabler:alert-triangle' className='text-danger flex-shrink-0' />
                    ) : (
                      <Icon icon='tabler:circle-check' className='text-success flex-shrink-0' />
                    )}
                    <span className='text-sm font-semibold truncate'>
                      {t('Kilos inconsistentes')}
                    </span>
                  </div>
                  <div className='flex items-center gap-3 flex-shrink-0'>
                    <span className='text-sm text-default-500'>
                      {hasIssue
                        ? `${kilosMismatchSettlements.length} ${t('liquidaciones afectadas')}`
                        : t('Sin problemas')}
                    </span>
                    {hasIssue && (
                      <Icon icon='tabler:chevron-down'
                        className={`text-xs text-default-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      />
                    )}
                  </div>
                </button>
                {isExpanded && (
                  <div className='px-4 pb-4 pt-2'>
                    <div className='flex flex-wrap items-start justify-between gap-4'>
                      <div className='text-sm text-default-500'>
                        <p>
                          {kilosMismatchSettlements.length}{' '}
                          {t(
                            'liquidaciones tienen kilos que no coinciden con peso bulto × cantidad de bultos. Son las siguientes referencias:'
                          )}
                        </p>
                        <p className='text-default-400 mt-1'>
                          {kilosMismatchSettlements
                            .map((settlement) =>
                              settlement.reference_number || `ID ${settlement.id}`
                            )
                            .join(', ')}
                        </p>
                      </div>
                      <Button
                        size='sm'
                        variant='flat'
                        className='flex-shrink-0'
                        onPress={() =>
                          setShowOnlyKilosMismatch(!showOnlyKilosMismatch)
                        }
                      >
                        {showOnlyKilosMismatch
                          ? t('Ver todas')
                          : `${t('Ver kilos inconsistentes')} (${kilosMismatchSettlements.length})`}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Negative values check — only shown when issues exist */}
          {(() => {
            const hasIssue = negativeValueSettlements.length > 0;
            if (!hasIssue) return null;
            const isExpanded = expandedCheck === 'negativevalues';
            return (
              <div>
                <button
                  type='button'
                  className='w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-default-50 transition-colors'
                  onClick={() =>
                    setExpandedCheck(isExpanded ? null : 'negativevalues')
                  }
                >
                  <div className='flex items-center gap-3 min-w-0'>
                    <Icon icon='tabler:alert-triangle' className='text-danger flex-shrink-0' />
                    <span className='text-sm font-semibold truncate'>
                      {t('Valores Negativos')}
                    </span>
                  </div>
                  <div className='flex items-center gap-3 flex-shrink-0'>
                    <span className='text-sm text-default-500'>
                      {`${negativeValueSettlements.length} ${t('liquidaciones afectadas')}`}
                    </span>
                    <Icon icon='tabler:chevron-down'
                      className={`text-xs text-default-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    />
                  </div>
                </button>
                {isExpanded && (
                  <div className='px-4 pb-4 pt-2'>
                    <div className='flex flex-wrap items-start justify-between gap-4'>
                      <div className='text-sm text-default-500'>
                        <p>
                          {negativeValueSettlements.length}{' '}
                          {t(
                            'liquidaciones tienen valores negativos en flete, comisión, impuestos u otros costos. Los valores deben ser positivos. Son las siguientes referencias:'
                          )}
                        </p>
                        <p className='text-default-400 mt-1'>
                          {negativeValueSettlements
                            .map((s) => s.reference_number || `ID ${s.id}`)
                            .join(', ')}
                        </p>
                      </div>
                      <Button
                        size='sm'
                        variant='flat'
                        className='flex-shrink-0'
                        onPress={() =>
                          setShowOnlyNegativeValues(!showOnlyNegativeValues)
                        }
                      >
                        {showOnlyNegativeValues
                          ? t('Ver todas')
                          : `${t('Ver valores negativos')} (${negativeValueSettlements.length})`}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {settlementViewMode === 'reference' ? (
          isLoading ? (
            <div className='w-full'>
              <div className='flex items-center h-12 bg-default-100 rounded-t-lg px-4'>
                {columns.map((column, index) => (
                  <div
                    key={index}
                    className={`flex-1 ${
                      column.uid === 'actions' ? 'flex justify-center' : ''
                    }`}
                  >
                    <Skeleton className='h-4 w-4/5 rounded-lg' />
                  </div>
                ))}
              </div>

              {Array(5)
                .fill(0)
                .map((_, rowIndex) => (
                  <div
                    key={rowIndex}
                    className='flex items-center h-16 px-4 border-b border-default-200'
                  >
                    {columns.map((column, colIndex) => (
                      <div
                        key={colIndex}
                        className={`flex-1 ${
                          column.uid === 'actions' ? 'flex justify-center' : ''
                        }`}
                      >
                        {column.uid === 'viewed' ? (
                          <Skeleton className='h-6 w-16 rounded-full' />
                        ) : column.uid === 'actions' ? (
                          <div className='flex gap-2 justify-center'>
                            <Skeleton className='h-8 w-8 rounded-full' />
                            <Skeleton className='h-8 w-8 rounded-full' />
                            <Skeleton className='h-8 w-8 rounded-full' />
                          </div>
                        ) : column.uid === 'status' ? (
                          <div className='flex gap-1'>
                            <Skeleton className='h-4 w-4 rounded-full' />
                            <Skeleton className='h-4 w-4 rounded-full' />
                            <Skeleton className='h-4 w-4 rounded-full' />
                            <Skeleton className='h-4 w-4 rounded-full' />
                          </div>
                        ) : column.uid === 'infoStatus' ? (
                          <Skeleton className='h-6 w-20 rounded-full' />
                        ) : (
                          <Skeleton className='h-4 w-4/5 rounded-lg' />
                        )}
                      </div>
                    ))}
                  </div>
                ))}

              <div className='flex justify-between items-center mt-4'>
                <Skeleton className='h-4 w-48 rounded-lg' />
                <div className='flex gap-2'>
                  <Skeleton className='h-8 w-20 rounded-lg' />
                  <div className='flex items-center gap-2'>
                    <Skeleton className='h-8 w-8 rounded-lg' />
                    <Skeleton className='h-8 w-8 rounded-lg' />
                    <Skeleton className='h-8 w-8 rounded-lg' />
                  </div>
                  <Skeleton className='h-8 w-20 rounded-lg' />
                </div>
              </div>
            </div>
          ) : (
            <>
              {getSortedSettlements.length === 0 ? (
                <EmptyState
                  title={t('No se encontraron liquidaciones con estos filtros')}
                />
              ) : (
                <div className='px-1'>
                  {renderSettlementsTable(getSortedSettlements)}
                </div>
              )}
            </>
          )
        ) : isSourceHierarchyLoading ? (
          <LoadingState label={t('Cargando documentos de origen')} />
        ) : sourceHierarchyError ? (
          <Alert variant='danger' showIcon>
            <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
              <p>{sourceHierarchyError}</p>
              <Button
                size='sm'
                variant='flat'
                onPress={() => void fetchSourceHierarchy()}
              >
                {t('Reintentar')}
              </Button>
            </div>
          </Alert>
        ) : (
          <>
            {exporterCostViewAvailable && (
              <div className='mb-3 flex justify-end'>
                <CostStandardToggle
                  value={sourceCostStandard}
                  onChange={setSourceCostStandard}
                />
              </div>
            )}
            <SettlementSourceViews
              mode={settlementViewMode}
              documents={displayedSourceDocuments}
              filteredSettlementIds={filteredSettlementIds}
              settlements={settlements}
              searchTerm={sourceSearchTerm}
              onOpenSettlement={(settlementId) =>
                setLocation(`/settlements/details/${settlementId}`)
              }
              onOpenPdf={(bucketId, storagePath, fileName, pageNumber) =>
                void openSourcePdf(bucketId, storagePath, fileName, pageNumber)
              }
            />
          </>
        )}
      </PageLayout>
      <ExcelUploadModal
        isOpen={isExcelUploadOpen}
        onClose={() => setIsExcelUploadOpen(false)}
        onSuccess={() => {
          // Refresh settlements after successful upload
          fetchSettlements();
        }}
      />
      <ManualUploadModal
        type='manual'
        isOpen={isManualUploadOpen}
        onClose={() => setIsManualUploadOpen(false)}
        onSuccess={() => {
          // Refresh settlements after successful upload
          fetchSettlements();
        }}
      />

      <ManualUploadModal
        type='shipments'
        isOpen={isShipmentsUploadOpen}
        onClose={() => setIsShipmentsUploadOpen(false)}
        onSuccess={() => {
          // Refresh settlements after successful upload
          fetchSettlements();
        }}
      />

      <ExcelMassiveUploadModal
        isOpen={isExcelMassiveUploadOpen}
        onClose={() => setIsExcelMassiveUploadOpen(false)}
        onSuccess={() => {
          // Refresh settlements after successful upload
          fetchSettlements();
        }}
      />

      <MultiShipmentUploadModal
        isOpen={isMultiShipmentUploadOpen}
        onClose={() => setIsMultiShipmentUploadOpen(false)}
        onSuccess={async () => {
          await Promise.all([fetchSettlements(), fetchSourceHierarchy()]);
        }}
      />

      <MassiveRevenueUploadModal
        isOpen={isMassiveRevenueUploadOpen}
        onClose={() => setIsMassiveRevenueUploadOpen(false)}
        onSuccess={() => {
          fetchSettlements();
        }}
      />

      <MassiveCostsUploadModal
        isOpen={isMassiveCostsUploadOpen}
        onClose={() => setIsMassiveCostsUploadOpen(false)}
        onSuccess={() => {
          fetchSettlements();
        }}
      />
    </div>
  );
};

export default LiquidationsDetails;
